import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

/** Paint jobs for the roadster. Prices in SOL unless `keys` is set. */
export interface Skin {
  id: string;
  name: string;
  price: number;
  keys?: number;
  body: string;
  accent: string;
  glass: string;
  /** a painted pattern on the body */
  pattern?: "solana" | "excel" | "holo" | "carbon" | "flames";
}

export const skins: Skin[] = [
  { id: "pearl", name: "Solana Pearl", price: 0, body: "#f4f5f8", accent: "#9945ff", glass: "#121722" },
  { id: "midnight", name: "Midnight", price: 300, body: "#22252f", accent: "#9945ff", glass: "#0b0d14" },
  { id: "red", name: "Red Coat", price: 500, body: "#d81f2a", accent: "#ffffff", glass: "#121722" },
  { id: "blue", name: "Deep Blue", price: 500, body: "#1f4fd8", accent: "#9fe8ff", glass: "#0f1626" },
  { id: "solana", name: "Solana", price: 1500, body: "#9945ff", accent: "#14f195", glass: "#120b24", pattern: "solana" },
  { id: "excel", name: "Excel", price: 2000, body: "#1d7044", accent: "#ffffff", glass: "#0b2416", pattern: "excel" },
  { id: "carbon", name: "Carbon", price: 2500, body: "#2a2c33", accent: "#14f195", glass: "#06070a", pattern: "carbon" },
  { id: "flames", name: "Hot Rod", price: 3000, body: "#151515", accent: "#ff8a00", glass: "#0a0a0a", pattern: "flames" },
  { id: "holo", name: "Holo", price: 0, keys: 30, body: "#c9b8ff", accent: "#ffffff", glass: "#1a1030", pattern: "holo" },
  { id: "gold", name: "Gold", price: 0, keys: 60, body: "#e8b923", accent: "#fff3c4", glass: "#2a1d05" }
];

export const skinById = (id: string): Skin => skins.find((s) => s.id === id) ?? skins[0];

export interface RoadsterParts {
  /** the whole car: position it */
  root: THREE.Group;
  /** the part that rolls in a kickflip (everything but the shadow) */
  body: THREE.Group;
  wheels: THREE.Object3D[];
  /** thruster flames under the car (point down when upright) */
  jets: THREE.Mesh[];
  /** rear booster flames */
  boosters: THREE.Mesh[];
  length: number;
  height: number;
  /**
   * A low-poly closed hull (~450 triangles: body + four wheel prisms) under `body` that casts the car's sun shadow in
   * place of the real meshes. Invisible (writes no colour or depth). detail "game": visible + castShadow, every real
   * mesh castShadow = false. detail "full": hidden (set visible = true to use it).
   */
  shadowProxy: THREE.Mesh;
  setSkin(skin: Skin): void;
}

export interface RoadsterOptions {
  /** "full" (default): shop previews, car.html. "game": the in-run car, ≤ 15k triangles, 10 draw calls. */
  detail?: RoadsterDetail;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Dimensions, metres. The car faces +z, y is up, +x is the car's left. Wheels touch y = 0.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
const Z_NOSE = 2.2;
const Z_TAIL = -2.2;
const LENGTH = Z_NOSE - Z_TAIL;
/** radius of the rounded edge around the flat tail panel */
const TAIL_R = 0.06;
const Z_TAIL_EDGE = Z_TAIL + TAIL_R;
/** the nose rounds off into a tip from here on */
const NOSE_START = 1.66;
const NOSE_POW = 2.2;
/** share of the section height the nose keeps at its tip (a short rounded bumper face) */
const NOSE_FACE = 0.42;
const Y_FLOOR = 0.17;
const WHEEL_R = 0.4;
const WHEEL_Y = 0.4;
const ARCH_R = 0.46;
/** inner wall of the wheel wells */
const TUB = 0.6;
const AXLE_F = 1.36;
const AXLE_R = -1.32;
const SILL = 0.22;
/** where an arch circle meets the sill line: the opening's straight front and back edges */
const ARCH_DZ = Math.sqrt(ARCH_R * ARCH_R - (WHEEL_Y - SILL) ** 2);
const Z_DIFFUSER = AXLE_R - ARCH_DZ;
/** the canopy: a teardrop of dark glass from the windshield base to behind the seats */
const CANOPY_FRONT = 1.0;
const CANOPY_WIDE = 0.12;
const CANOPY_BACK = -1.4;
const CANOPY_W = 0.84;
const CANOPY_P = 2.2;
/** body-colour frame around the glass (plan-view inset) */
const FRAME = 0.03;
/** texture v per metre of section arc */
const V_SCALE = 1.7;
/** a texture spot that is plain body colour on every pattern (mirror pods, louvres) */
const PLAIN_UV: V2 = [0.3, 0.3];

const WHEELS = [
  { name: "wheel-front-left", x: 0.835, z: AXLE_F, front: true },
  { name: "wheel-front-right", x: -0.835, z: AXLE_F, front: true },
  { name: "wheel-back-left", x: 0.84, z: AXLE_R, front: false },
  { name: "wheel-back-right", x: -0.84, z: AXLE_R, front: false }
];
const TYRE_HALF = { front: 0.135, back: 0.155 };

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Small math
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
type V2 = [number, number];
const clamp = (v: number, a: number, b: number): number => Math.min(b, Math.max(a, v));
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const smoothstep = (a: number, b: number, v: number): number => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };

/**
 * Build detail. "full": the shop previews and car.html (every bolt). "game": the in-run car, seen from the chase camera
 * (~8 m up and behind) — the same shapes tessellated ~4× coarser, merged into one mesh per material (see buildGameCar).
 * LOD is only switched while carGeometry() / the decals build; every tessellation count below reads it through lod().
 */
export type RoadsterDetail = "full" | "game";
let LOD: RoadsterDetail = "full";
const lod = (full: number, game: number): number => (LOD === "game" ? game : full);
/** a rounded box, or (game) a plain box: the 2–5 cm radii are invisible from the chase camera */
const rbox = (w: number, h: number, d: number, seg: number, r: number): THREE.BufferGeometry => (LOD === "game" ? new THREE.BoxGeometry(w, h, d) : new RoundedBoxGeometry(w, h, d, seg, r));

/** A smooth, overshoot-free curve through keyframes (monotone cubic Hermite). Keys sorted by z. */
function track(keys: Array<[number, number]>): (z: number) => number {
  const xs = keys.map((k) => k[0]), ys = keys.map((k) => k[1]);
  const n = xs.length;
  const d: number[] = [];
  for (let i = 0; i < n - 1; i++) d.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]));
  const m: number[] = new Array<number>(n).fill(0);
  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (d[i - 1] * d[i] <= 0) continue;
    const h0 = xs[i] - xs[i - 1], h1 = xs[i + 1] - xs[i];
    const w1 = 2 * h1 + h0, w2 = h1 + 2 * h0;
    m[i] = (w1 + w2) / (w1 / d[i - 1] + w2 / d[i]);
  }
  return (z: number): number => {
    if (z <= xs[0]) return ys[0];
    if (z >= xs[n - 1]) return ys[n - 1];
    let i = 0;
    while (z > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i], t = (z - xs[i]) / h, t2 = t * t, t3 = t2 * t;
    return (2 * t3 - 3 * t2 + 1) * ys[i] + (t3 - 2 * t2 + t) * h * m[i] + (-2 * t3 + 3 * t2) * ys[i + 1] + (t3 - t2) * h * m[i + 1];
  };
}

/** Centripetal Catmull-Rom between p1 and p2. */
function catmullRom(p0: V2, p1: V2, p2: V2, p3: V2, t: number): V2 {
  const t01 = Math.max(1e-9, Math.sqrt(Math.hypot(p1[0] - p0[0], p1[1] - p0[1])));
  const t12 = Math.max(1e-9, Math.sqrt(Math.hypot(p2[0] - p1[0], p2[1] - p1[1])));
  const t23 = Math.max(1e-9, Math.sqrt(Math.hypot(p3[0] - p2[0], p3[1] - p2[1])));
  const out: V2 = [0, 0];
  const t2 = t * t, t3 = t2 * t;
  for (let k = 0; k < 2; k++) {
    let m1 = (p1[k] - p0[k]) / t01 - (p2[k] - p0[k]) / (t01 + t12) + (p2[k] - p1[k]) / t12;
    let m2 = (p2[k] - p1[k]) / t12 - (p3[k] - p1[k]) / (t12 + t23) + (p3[k] - p2[k]) / t23;
    m1 *= t12;
    m2 *= t12;
    out[k] = (2 * t3 - 3 * t2 + 1) * p1[k] + (t3 - 2 * t2 + t) * m1 + (-2 * t3 + 3 * t2) * p2[k] + (t3 - t2) * m2;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// The body surface. Every station z has a half cross-section (x ≥ 0) through seven control points, sill to centre:
//   B sill · L lower side · M widest · U shoulder · C crest (fender top / door top) · H deck · T centre line
// Each coordinate is a keyframed curve along z; the canopy dome is added on top of the deck.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
const W = track([[-2.2, 0.95], [-2.0, 0.995], [-1.7, 1.018], [-1.32, 1.024], [-0.95, 1.005], [-0.55, 0.958], [-0.1, 0.93], [0.35, 0.932], [0.85, 0.958], [1.36, 0.982], [1.75, 0.975], [2.2, 0.96]]);
const YM = track([[-2.2, 0.62], [-1.8, 0.66], [-1.32, 0.7], [-0.9, 0.62], [-0.5, 0.52], [0.0, 0.5], [0.6, 0.55], [1.0, 0.64], [1.36, 0.68], [1.65, 0.6], [1.85, 0.5], [2.05, 0.43], [2.2, 0.4]]);
const YB = track([[-2.2, 0.42], [-2.05, 0.38], [-1.85, 0.3], [-1.6, 0.24], [-1.0, 0.225], [0.0, 0.215], [1.0, 0.225], [1.6, 0.232], [1.9, 0.222], [2.2, 0.22]]);
const TUCK_B = track([[-2.2, 0.05], [-1.5, 0.06], [0.0, 0.075], [1.5, 0.06], [2.2, 0.07]]);
const TUCK_L = track([[-2.2, 0.012], [-1.0, 0.018], [-0.6, 0.034], [-0.2, 0.03], [0.4, 0.02], [1.2, 0.015], [2.2, 0.014]]);
const TUMBLE = track([[-2.2, 0.06], [-1.32, 0.05], [-0.7, 0.05], [0.0, 0.05], [0.6, 0.055], [1.36, 0.05], [2.2, 0.06]]);
const YU = track([[-2.2, 0.8], [-1.8, 0.84], [-1.32, 0.885], [-0.8, 0.82], [-0.2, 0.795], [0.5, 0.8], [1.0, 0.84], [1.36, 0.88], [1.6, 0.8], [1.8, 0.7], [2.0, 0.57], [2.2, 0.49]]);
const XC = track([[-2.2, 0.72], [-1.7, 0.8], [-1.32, 0.84], [-0.9, 0.86], [-0.5, 0.87], [0.0, 0.855], [0.5, 0.85], [0.9, 0.82], [1.36, 0.78], [1.8, 0.74], [2.2, 0.66]]);
const YC = track([[-2.2, 0.86], [-1.8, 0.92], [-1.32, 0.975], [-0.85, 0.87], [-0.2, 0.812], [0.5, 0.82], [1.0, 0.88], [1.36, 0.945], [1.6, 0.88], [1.8, 0.775], [2.0, 0.645], [2.2, 0.53]]);
const YH = track([[-2.2, 0.88], [-2.05, 0.872], [-1.8, 0.878], [-1.32, 0.9], [-0.8, 0.845], [0.0, 0.825], [0.8, 0.815], [1.36, 0.78], [1.6, 0.735], [1.8, 0.665], [2.0, 0.585], [2.2, 0.5]]);
const YT = track([[-2.2, 0.885], [-2.05, 0.874], [-1.8, 0.873], [-1.32, 0.89], [-0.8, 0.845], [0.0, 0.828], [0.8, 0.8], [1.36, 0.755], [1.6, 0.712], [1.8, 0.652], [2.0, 0.572], [2.2, 0.49]]);
const CANOPY_H = track([[-1.4, 0], [-1.2, 0.08], [-0.9, 0.2], [-0.55, 0.33], [-0.2, 0.405], [0.15, 0.38], [0.5, 0.27], [0.8, 0.13], [1.0, 0]]);

function controlPoints(z: number): V2[] {
  const w = W(z), ym = YM(z), yb = YB(z), xc = XC(z);
  const pts: V2[] = [
    [w - TUCK_B(z), yb],
    [w - TUCK_L(z), yb + (ym - yb) * 0.42],
    [w, ym],
    [w - TUMBLE(z), YU(z)],
    [xc, YC(z)],
    [xc * 0.5, YH(z)],
    [0, YT(z)]
  ];
  if (z > NOSE_START) {
    const d = Math.min(1, (z - NOSE_START) / (Z_NOSE - NOSE_START));
    const k = Math.pow(Math.max(0, 1 - Math.pow(d, NOSE_POW)), 1 / NOSE_POW);
    const ky = NOSE_FACE + (1 - NOSE_FACE) * k;
    const mid = lerp(pts[0][1], pts[6][1], 0.45);
    for (const p of pts) { p[0] *= k; p[1] = mid + (p[1] - mid) * ky; }
  }
  return pts;
}

/** the station where the nose has narrowed to share k of its width (uniform steps across the blunt tip) */
function noseZ(k: number): number {
  return NOSE_START + (Z_NOSE - NOSE_START) * Math.pow(Math.max(0, 1 - Math.pow(clamp(k, 0, 1), NOSE_POW)), 1 / NOSE_POW);
}

/** Plan-view half width of the canopy teardrop. */
function canopyWidth(z: number): number {
  if (z >= CANOPY_FRONT || z <= CANOPY_BACK) return 0;
  if (z >= CANOPY_WIDE) {
    const d = (z - CANOPY_WIDE) / (CANOPY_FRONT - CANOPY_WIDE);
    return CANOPY_W * Math.pow(Math.max(0, 1 - Math.pow(d, 2.2)), 1 / 2.2);
  }
  const d = (CANOPY_WIDE - z) / (CANOPY_WIDE - CANOPY_BACK);
  return CANOPY_W * Math.pow(Math.max(0, 1 - Math.pow(d, 1.8)), 0.8);
}

/** Plan-view half width of the glass: the teardrop inset by the frame, measured in the plane (so the frame stays even). */
let glassTable: Float64Array | undefined;
const GLASS_N = 900;
function glassWidth(z: number): number {
  if (!glassTable) {
    const outline: V2[] = [];
    for (let i = 0; i <= 1200; i++) { const zz = lerp(CANOPY_BACK, CANOPY_FRONT, i / 1200); outline.push([canopyWidth(zz), zz]); }
    const dist = (px: number, pz: number): number => {
      let best = Infinity;
      for (let i = 0; i < outline.length - 1; i++) {
        const [ax, az] = outline[i], [bx, bz] = outline[i + 1];
        if (Math.min(az, bz) > pz + FRAME * 2 || Math.max(az, bz) < pz - FRAME * 2) continue;
        const vx = bx - ax, vz = bz - az, l2 = vx * vx + vz * vz;
        const t = l2 > 0 ? clamp(((px - ax) * vx + (pz - az) * vz) / l2, 0, 1) : 0;
        best = Math.min(best, Math.hypot(px - ax - vx * t, pz - az - vz * t));
      }
      return best;
    };
    glassTable = new Float64Array(GLASS_N);
    for (let i = 0; i < GLASS_N; i++) {
      const zz = lerp(CANOPY_BACK, CANOPY_FRONT, i / (GLASS_N - 1));
      const cw = canopyWidth(zz);
      if (cw <= 0 || dist(0, zz) < FRAME) continue;
      let lo = 0, hi = cw;
      for (let k = 0; k < 26; k++) { const mid = (lo + hi) / 2; if (dist(mid, zz) >= FRAME) lo = mid; else hi = mid; }
      glassTable[i] = lo;
    }
  }
  const t = (z - CANOPY_BACK) / (CANOPY_FRONT - CANOPY_BACK) * (GLASS_N - 1);
  if (t <= 0 || t >= GLASS_N - 1) return 0;
  const i = Math.floor(t);
  return lerp(glassTable[i], glassTable[i + 1], t - i);
}

interface Section {
  z: number;
  x: number[];
  y: number[];
  /** arc length from the sill */
  s: number[];
  /** sampling weight: arc length plus a share per radian of turning, so rows crowd into the curves */
  w: number[];
  total: number;
  /** index where the top (shoulder → centre) begins, and of the crest */
  top: number;
  crest: number;
  sCut: number;
  sDome: number;
  sGlass: number;
}

const SEG = 18;
const TURN_WEIGHT = 0.045;

function sectionAt(z: number, cutY: number | null): Section {
  const cp = controlPoints(z);
  const ext: V2[] = [[2 * cp[0][0] - cp[1][0], 2 * cp[0][1] - cp[1][1]], ...cp, [-cp[5][0], cp[5][1]]];
  const x: number[] = [], y: number[] = [];
  let top = 0;
  for (let k = 0; k < cp.length - 1; k++) {
    if (k === 3) top = x.length;
    for (let i = 0; i < SEG; i++) {
      const p = catmullRom(ext[k], ext[k + 1], ext[k + 2], ext[k + 3], i / SEG);
      x.push(p[0]);
      y.push(p[1]);
    }
  }
  x.push(cp[6][0]);
  y.push(cp[6][1]);
  // the canopy: an exact vertex at its base and at the glass edge, then the dome on top of the deck
  const cw = canopyWidth(z), gw = Math.min(glassWidth(z), cw - 0.004), ch = CANOPY_H(z);
  let dome = -1, glass = -1;
  const insertAt = (xv: number): number => {
    for (let i = top; i < x.length - 1; i++) {
      if (x[i] >= xv && x[i + 1] < xv) {
        const t = (x[i] - xv) / (x[i] - x[i + 1]);
        x.splice(i + 1, 0, xv);
        y.splice(i + 1, 0, lerp(y[i], y[i + 1], t));
        return i + 1;
      }
    }
    return -1;
  };
  if (cw > 1e-4 && ch > 0) {
    dome = insertAt(cw);
    if (gw > 1e-4) glass = insertAt(gw);
    for (let i = top; i < x.length; i++) if (x[i] < cw) y[i] += ch * (1 - Math.pow(x[i] / cw, CANOPY_P));
  }
  const n = x.length;
  const turn = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    if (i === dome || i === glass) continue;
    const ax = x[i] - x[i - 1], ay = y[i] - y[i - 1], bx = x[i + 1] - x[i], by = y[i + 1] - y[i];
    if (ax * ax + ay * ay < 1e-14 || bx * bx + by * by < 1e-14) continue;
    let d = Math.abs(Math.atan2(by, bx) - Math.atan2(ay, ax));
    if (d > Math.PI) d = 2 * Math.PI - d;
    turn[i] = d;
  }
  const s = new Array<number>(n).fill(0), w = new Array<number>(n).fill(0);
  for (let i = 1; i < n; i++) {
    const d = Math.hypot(x[i] - x[i - 1], y[i] - y[i - 1]);
    s[i] = s[i - 1] + d;
    w[i] = w[i - 1] + d + TURN_WEIGHT * 0.5 * (turn[i - 1] + turn[i]);
  }
  const total = s[n - 1];
  let crest = 0;
  for (let i = 0; i <= Math.min(n - 1, top + SEG + 1); i++) if (y[i] >= y[crest]) crest = i;
  let sCut = 0;
  if (cutY !== null && y[0] < cutY) {
    sCut = s[crest];
    for (let i = 1; i <= crest; i++) {
      if (y[i] >= cutY) { sCut = lerp(s[i - 1], s[i], (cutY - y[i - 1]) / (y[i] - y[i - 1])); break; }
    }
  }
  return { z, x, y, s, w, total, top, crest, sCut, sDome: dome >= 0 ? s[dome] : total, sGlass: glass >= 0 ? s[glass] : total };
}

function locate(arr: number[], v: number): number {
  let lo = 0, hi = arr.length - 1;
  if (v <= arr[0]) return 0;
  if (v >= arr[hi]) return hi - 1;
  while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (arr[mid] <= v) lo = mid; else hi = mid; }
  return lo;
}
function pointAt(sec: Section, sv: number): V2 {
  const i = locate(sec.s, sv), d = sec.s[i + 1] - sec.s[i];
  const t = d > 1e-12 ? clamp((sv - sec.s[i]) / d, 0, 1) : 0;
  return [lerp(sec.x[i], sec.x[i + 1], t), lerp(sec.y[i], sec.y[i + 1], t)];
}
function wOfS(sec: Section, sv: number): number {
  const i = locate(sec.s, sv), d = sec.s[i + 1] - sec.s[i];
  return lerp(sec.w[i], sec.w[i + 1], d > 1e-12 ? clamp((sv - sec.s[i]) / d, 0, 1) : 0);
}
function sOfW(sec: Section, wv: number): number {
  const i = locate(sec.w, wv), d = sec.w[i + 1] - sec.w[i];
  return lerp(sec.s[i], sec.s[i + 1], d > 1e-12 ? clamp((wv - sec.w[i]) / d, 0, 1) : 0);
}

type Band = 0 | 1 | 2;
/** 0 = painted body (sill → canopy base), 1 = the painted frame, 2 = the glass. Over an arch the body rows are the
 *  same as everywhere else, only clamped to the arch lip (so neighbouring patches share their rows: no cracks). */
function bandRange(sec: Section, band: Band): V2 {
  return band === 0 ? [0, sec.sDome] : band === 1 ? [sec.sDome, sec.sGlass] : [sec.sGlass, sec.total];
}
/** closest point to (px, py) on a section, between arc lengths lo and hi */
function closestOn(sec: Section, px: number, py: number, lo: number, hi: number): V2 {
  let best: V2 = pointAt(sec, lo), bestD = Infinity;
  const i0 = locate(sec.s, lo), i1 = Math.min(sec.x.length - 2, locate(sec.s, hi));
  for (let i = i0; i <= i1; i++) {
    const ax = sec.x[i], ay = sec.y[i], vx = sec.x[i + 1] - ax, vy = sec.y[i + 1] - ay, l2 = vx * vx + vy * vy;
    let t = l2 > 1e-14 ? ((px - ax) * vx + (py - ay) * vy) / l2 : 0;
    const sa = sec.s[i], sb = sec.s[i + 1];
    t = clamp(t, sb > sa ? clamp((lo - sa) / (sb - sa), 0, 1) : 0, sb > sa ? clamp((hi - sa) / (sb - sa), 0, 1) : 1);
    const qx = ax + vx * t, qy = ay + vy * t, d = (qx - px) ** 2 + (qy - py) ** 2;
    if (d < bestD) { bestD = d; best = [qx, qy]; }
  }
  return best;
}
function rowS(sec: Section, band: Band, tau: number): number {
  const sv = bandS(sec, band, tau);
  return band === 0 ? Math.max(sv, sec.sCut) : sv;
}
function bandS(sec: Section, band: Band, tau: number): number {
  const [a, b] = bandRange(sec, band);
  if (b - a < 1e-9) return a;
  return sOfW(sec, lerp(wOfS(sec, a), wOfS(sec, b), tau));
}

/** top of an arch opening above station z (the circle, extended for finite differences) */
function archTop(z: number): number {
  const zw = Math.abs(z - AXLE_F) < Math.abs(z - AXLE_R) ? AXLE_F : AXLE_R;
  return WHEEL_Y + Math.sqrt(Math.max(0, ARCH_R * ARCH_R - (z - zw) ** 2));
}

// surface lookups for details (uncut sections, cached)
const sectionCache = new Map<number, Section>();
function surf(z: number): Section {
  const key = Math.round(z * 2e4);
  let sec = sectionCache.get(key);
  if (!sec) { sec = sectionAt(key / 2e4, null); sectionCache.set(key, sec); }
  return sec;
}
/** arc length where the outer side (sill → crest) passes height yv */
function sideS(sec: Section, yv: number): number {
  if (yv <= sec.y[0]) return 0;
  for (let i = 1; i <= sec.crest; i++) {
    if (sec.y[i] >= yv) { const d = sec.y[i] - sec.y[i - 1]; return lerp(sec.s[i - 1], sec.s[i], d > 1e-12 ? (yv - sec.y[i - 1]) / d : 0); }
  }
  return sec.s[sec.crest];
}
/** arc length where the top (shoulder → centre line) passes x = xv */
function topS(sec: Section, xv: number): number {
  for (let i = sec.top; i < sec.x.length - 1; i++) {
    if (sec.x[i] >= xv && sec.x[i + 1] <= xv) { const d = sec.x[i] - sec.x[i + 1]; return lerp(sec.s[i], sec.s[i + 1], d > 1e-12 ? (sec.x[i] - xv) / d : 0); }
  }
  return xv > sec.x[sec.top] ? sec.s[sec.top] : sec.total;
}
const sideXY = (z: number, yv: number): THREE.Vector3 => { const sec = surf(z); const p = pointAt(sec, sideS(sec, yv)); return new THREE.Vector3(p[0], p[1], z); };
const topXZ = (xv: number, z: number): THREE.Vector3 => { const sec = surf(z); const p = pointAt(sec, topS(sec, xv)); return new THREE.Vector3(p[0], p[1], z); };
const sideX = (z: number, yv: number): number => { const sec = surf(z); return pointAt(sec, sideS(sec, yv))[0]; };
const crestY = (z: number): number => { const sec = surf(z); return sec.y[sec.crest]; };

interface SP { p: THREE.Vector3; n: THREE.Vector3 }
/** a point on a parametrised patch of the body with its normal, oriented along `out` */
function surfacePoint(f: (a: number, b: number) => THREE.Vector3, a: number, b: number, out: THREE.Vector3, e = 0.003): SP {
  const p = f(a, b);
  const da = f(a + e, b).sub(f(a - e, b));
  const db = f(a, b + e).sub(f(a, b - e));
  const n = new THREE.Vector3().crossVectors(da, db);
  if (n.lengthSq() < 1e-14) n.copy(out);
  n.normalize();
  if (n.dot(out) < 0) n.negate();
  return { p, n };
}
const onSide = (z: number, yv: number): SP => surfacePoint(sideXY, z, yv, new THREE.Vector3(1, 0, Math.max(0, z - 1.6) * 2));
const onTop = (xv: number, z: number): SP => surfacePoint(topXZ, xv, z, new THREE.Vector3(0, 1, 0));

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Mesh assembly: one buffer per material, triangles oriented by their vertex normals
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
class Mesher {
  readonly pos: number[] = [];
  readonly nor: number[] = [];
  readonly uvs: number[] = [];
  readonly col: number[] = [];
  readonly idx: number[] = [];
  color: [number, number, number] = [1, 1, 1];

  get count(): number { return this.pos.length / 3; }

  vert(x: number, y: number, z: number, nx: number, ny: number, nz: number, u = 0, v = 0): number {
    const l = Math.hypot(nx, ny, nz) || 1;
    this.pos.push(x, y, z);
    this.nor.push(nx / l, ny / l, nz / l);
    this.uvs.push(u, v);
    this.col.push(this.color[0], this.color[1], this.color[2]);
    return this.count - 1;
  }

  vp(p: THREE.Vector3, n: THREE.Vector3, u = 0, v = 0): number { return this.vert(p.x, p.y, p.z, n.x, n.y, n.z, u, v); }

  /** adds a triangle facing the way its vertex normals point; skips slivers */
  tri(a: number, b: number, c: number): void {
    const P = this.pos, N = this.nor;
    const ax = P[a * 3], ay = P[a * 3 + 1], az = P[a * 3 + 2];
    const e1x = P[b * 3] - ax, e1y = P[b * 3 + 1] - ay, e1z = P[b * 3 + 2] - az;
    const e2x = P[c * 3] - ax, e2y = P[c * 3 + 1] - ay, e2z = P[c * 3 + 2] - az;
    const fx = e1y * e2z - e1z * e2y, fy = e1z * e2x - e1x * e2z, fz = e1x * e2y - e1y * e2x;
    if (fx * fx + fy * fy + fz * fz < 1e-18) return;
    const nx = N[a * 3] + N[b * 3] + N[c * 3], ny = N[a * 3 + 1] + N[b * 3 + 1] + N[c * 3 + 1], nz = N[a * 3 + 2] + N[b * 3 + 2] + N[c * 3 + 2];
    if (fx * nx + fy * ny + fz * nz < 0) this.idx.push(a, c, b); else this.idx.push(a, b, c);
  }

  quad(a: number, b: number, c: number, d: number): void { this.tri(a, b, c); this.tri(a, c, d); }

  grid(ids: number[][]): void {
    for (let i = 0; i < ids.length - 1; i++) {
      for (let j = 0; j < ids[i].length - 1; j++) this.quad(ids[i][j], ids[i][j + 1], ids[i + 1][j + 1], ids[i + 1][j]);
    }
  }

  /** appends a three.js geometry, transformed (optionally pinned to one texture coordinate) */
  add(g: THREE.BufferGeometry, m?: THREE.Matrix4, uvPin?: V2): void {
    const pos = g.getAttribute("position"), nor = g.getAttribute("normal"), uv = g.getAttribute("uv"), col = g.getAttribute("color");
    const nm = m ? new THREE.Matrix3().getNormalMatrix(m) : undefined;
    const base = this.count;
    const v = new THREE.Vector3(), n = new THREE.Vector3();
    const keep = this.color;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      if (m) v.applyMatrix4(m);
      if (nor) n.fromBufferAttribute(nor, i); else n.set(0, 1, 0);
      if (nm) n.applyMatrix3(nm);
      if (col) this.color = [col.getX(i), col.getY(i), col.getZ(i)];
      this.vert(v.x, v.y, v.z, n.x, n.y, n.z, uvPin ? uvPin[0] : uv ? uv.getX(i) : 0, uvPin ? uvPin[1] : uv ? uv.getY(i) : 0);
    }
    this.color = keep;
    const flip = m ? m.determinant() < 0 : false;
    const index = g.getIndex();
    const count = index ? index.count : pos.count;
    for (let i = 0; i < count; i += 3) {
      const a = (index ? index.getX(i) : i) + base, b = (index ? index.getX(i + 1) : i + 1) + base, c = (index ? index.getX(i + 2) : i + 2) + base;
      if (flip) this.idx.push(a, c, b); else this.idx.push(a, b, c);
    }
  }

  /** mirrors everything so far across x = 0 (the other half of the car) */
  mirrorX(): void {
    const n = this.count, t = this.idx.length;
    for (let i = 0; i < n; i++) {
      this.pos.push(-this.pos[i * 3], this.pos[i * 3 + 1], this.pos[i * 3 + 2]);
      this.nor.push(-this.nor[i * 3], this.nor[i * 3 + 1], this.nor[i * 3 + 2]);
      this.uvs.push(this.uvs[i * 2], this.uvs[i * 2 + 1]);
      this.col.push(this.col[i * 3], this.col[i * 3 + 1], this.col[i * 3 + 2]);
    }
    for (let i = 0; i < t; i += 3) this.idx.push(this.idx[i] + n, this.idx[i + 2] + n, this.idx[i + 1] + n);
  }

  geometry(colors = false): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(this.uvs, 2));
    if (colors) g.setAttribute("color", new THREE.Float32BufferAttribute(this.col, 3));
    g.setIndex(this.idx);
    g.computeBoundingSphere();
    return g;
  }
}

/** A ribbon lying on the body along a path (panel gaps, light strips, pinstripes). */
function ribbon(m: Mesher, path: SP[], width: number | ((t: number) => number), lift: number, offset = 0, uvPin?: V2): void {
  if (path.length < 2) return;
  const lengths = [0];
  for (let i = 1; i < path.length; i++) lengths.push(lengths[i - 1] + path[i].p.distanceTo(path[i - 1].p));
  const total = lengths[lengths.length - 1] || 1;
  let prev: [number, number] | null = null;
  const tangent = new THREE.Vector3(), side = new THREE.Vector3(), c = new THREE.Vector3();
  for (let i = 0; i < path.length; i++) {
    tangent.subVectors(path[Math.min(path.length - 1, i + 1)].p, path[Math.max(0, i - 1)].p).normalize();
    const { p, n } = path[i];
    const wv = typeof width === "number" ? width : width(lengths[i] / total);
    side.crossVectors(tangent, n).normalize();
    c.copy(p).addScaledVector(n, lift).addScaledVector(side, offset);
    const a = m.vert(c.x + side.x * wv / 2, c.y + side.y * wv / 2, c.z + side.z * wv / 2, n.x, n.y, n.z, uvPin ? uvPin[0] : lengths[i] / total, uvPin ? uvPin[1] : 1);
    const b = m.vert(c.x - side.x * wv / 2, c.y - side.y * wv / 2, c.z - side.z * wv / 2, n.x, n.y, n.z, uvPin ? uvPin[0] : lengths[i] / total, uvPin ? uvPin[1] : 0);
    if (prev) m.quad(prev[0], prev[1], b, a);
    prev = [a, b];
  }
}

/** A polyline with its corners rounded (quadratic), resampled every `step`. */
function roundedPath(points: V2[], radius: number, closed: boolean, step = 0.02): V2[] {
  step *= lod(1, 2.5);
  const arcSteps = lod(6, 3);
  const clean = points.filter((p, i) => i === 0 || Math.hypot(p[0] - points[i - 1][0], p[1] - points[i - 1][1]) > 1e-6);
  if (closed && clean.length > 1 && Math.hypot(clean[0][0] - clean[clean.length - 1][0], clean[0][1] - clean[clean.length - 1][1]) < 1e-6) clean.pop();
  const pts = closed ? [...clean, clean[0]] : clean;
  const out: V2[] = [];
  const push = (p: V2): void => { const l = out[out.length - 1]; if (!l || Math.hypot(p[0] - l[0], p[1] - l[1]) > 1e-6) out.push(p); };
  const corner = (i: number): { a: V2; b: V2 } => {
    const p = pts[i];
    const prev = closed && i === 0 ? pts[pts.length - 2] : pts[i - 1];
    const next = closed && i === pts.length - 1 ? pts[1] : pts[i + 1];
    if (!prev || !next) return { a: p, b: p };
    const l1 = Math.hypot(p[0] - prev[0], p[1] - prev[1]), l2 = Math.hypot(next[0] - p[0], next[1] - p[1]);
    const r = Math.min(radius, l1 * 0.5, l2 * 0.5);
    return { a: [p[0] + (prev[0] - p[0]) * r / l1, p[1] + (prev[1] - p[1]) * r / l1], b: [p[0] + (next[0] - p[0]) * r / l2, p[1] + (next[1] - p[1]) * r / l2] };
  };
  const cs = pts.map((_, i) => corner(i));
  for (let i = 0; i < pts.length - 1; i++) {
    const start = cs[i].b, end = cs[i + 1].a;
    if (i === 0) push(closed ? cs[0].b : pts[0]);
    const len = Math.hypot(end[0] - start[0], end[1] - start[1]);
    const nSteps = Math.max(1, Math.ceil(len / step));
    for (let k = 1; k <= nSteps; k++) push([lerp(start[0], end[0], k / nSteps), lerp(start[1], end[1], k / nSteps)]);
    if (i + 1 < pts.length - 1 || closed) {
      const { a, b } = cs[i + 1], p = pts[i + 1];
      for (let k = 1; k <= arcSteps; k++) { const t = k / arcSteps; push([(1 - t) * (1 - t) * a[0] + 2 * (1 - t) * t * p[0] + t * t * b[0], (1 - t) * (1 - t) * a[1] + 2 * (1 - t) * t * p[1] + t * t * b[1]]); }
    }
  }
  return out;
}

/** Lathe around the x axis. Profile points are [radius, x]; walk so the outside is on the left of travel in an
 *  (x →, r ↑) plot. A repeated point makes a hard edge. */
function revolveX(profile: V2[], segments: number, phi0 = 0, phiLen = Math.PI * 2): THREE.BufferGeometry {
  if (LOD === "game") segments = Math.max(phiLen < Math.PI ? 3 : 10, Math.round(segments / 3));
  const m = new Mesher();
  const normals: V2[] = profile.map((p, i) => {
    const dup = (a: number, b: number): boolean => a >= 0 && b < profile.length && Math.abs(profile[a][0] - profile[b][0]) < 1e-9 && Math.abs(profile[a][1] - profile[b][1]) < 1e-9;
    let a = Math.max(0, i - 1), b = Math.min(profile.length - 1, i + 1);
    if (dup(i, i + 1)) b = i;
    if (dup(i - 1, i)) a = i;
    let dr = profile[b][0] - profile[a][0], dx = profile[b][1] - profile[a][1];
    if (Math.hypot(dr, dx) < 1e-12) { dr = 0; dx = 1; }
    const l = Math.hypot(dr, dx);
    return [dx / l, -dr / l];               // [n_r, n_x]: the left of travel
  });
  const rows: number[][] = [];
  const full = phiLen >= Math.PI * 2 - 1e-6;
  for (let s = 0; s <= segments; s++) {
    const phi = phi0 + phiLen * (s / segments);
    const c = Math.cos(phi), sn = Math.sin(phi);
    rows.push(profile.map((p, i) => m.vert(p[1], p[0] * c, p[0] * sn, normals[i][1], normals[i][0] * c, normals[i][0] * sn, s / segments, i / (profile.length - 1))));
  }
  if (full) rows[segments] = rows[0];
  m.grid(rows);
  return m.geometry();
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// The shell: painted body, frame and glass bands, wheel arches cut out, rounded tail edge and tail panel
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
const ROWS: Record<Band, number> = { 0: 34, 1: 3, 2: 14 };
const GAME_ROWS: Record<Band, number> = { 0: 13, 1: 3, 2: 6 };
const EPS = 0.003;
const uOf = (z: number): number => (z - Z_TAIL) / LENGTH;

function skinStations(): number[] {
  const set = new Set<number>();
  const add = (z: number): void => { if (z >= Z_TAIL_EDGE - 1e-9 && z <= Z_NOSE + 1e-9) set.add(Math.round(z * 1e5) / 1e5); };
  const step = lod(0.04, 0.1), tip = lod(16, 5), nose = lod(20, 7), cap = lod(12, 4);
  for (let z = Z_TAIL_EDGE; z < Z_NOSE - 0.1; z += step) add(z);
  for (let i = 0; i <= tip; i++) add(Z_NOSE - 0.12 * (1 - i / tip) ** 2);
  for (let i = 1; i <= nose; i++) add(noseZ(1 - i / nose));
  for (let i = 0; i <= cap; i++) { add(CANOPY_FRONT - 0.1 * (i / cap) ** 2); add(CANOPY_BACK + 0.1 * (i / cap) ** 2); }
  for (const a of [AXLE_R, AXLE_F]) { add(a - ARCH_DZ); add(a + ARCH_DZ); }
  add(Z_TAIL_EDGE);
  add(Z_NOSE);
  return [...set].sort((a, b) => a - b);
}

interface TailRing { x: number; y: number; z: number; n: THREE.Vector3; s: number }

function buildShell(paint: Mesher, glass: Mesher): { rings: TailRing[][] } {
  const zs = skinStations();
  const patches: Array<[number, number, boolean]> = [
    [Z_TAIL_EDGE, AXLE_R - ARCH_DZ, false], [AXLE_R - ARCH_DZ, AXLE_R + ARCH_DZ, true], [AXLE_R + ARCH_DZ, AXLE_F - ARCH_DZ, false],
    [AXLE_F - ARCH_DZ, AXLE_F + ARCH_DZ, true], [AXLE_F + ARCH_DZ, Z_NOSE, false]
  ];
  const tipNormal = new THREE.Vector3(0, 0.12, 1).normalize();
  const firstRow: Array<{ x: number; y: number; s: number; n: THREE.Vector3 }> = [];
  for (const [z0, z1, cut] of patches) {
    const list = zs.filter((z) => z >= z0 - 1e-4 && z <= z1 + 1e-4);
    const secs = list.map((z) => ({
      z,
      sec: sectionAt(z, cut ? archTop(z) : null),
      prev: sectionAt(z - EPS, cut ? archTop(z - EPS) : null),
      next: sectionAt(Math.min(Z_NOSE, z + EPS), cut ? archTop(z + EPS) : null)
    }));
    for (const band of [0, 1, 2] as Band[]) {
      if (secs.every(({ sec }) => { const [a, b] = bandRange(sec, band); return b - a < 1e-6; })) continue;
      const target = band === 2 ? glass : paint;
      const ids: number[][] = [];
      for (const { z, sec, prev, next } of secs) {
        const row: number[] = [];
        const [a0, b] = bandRange(sec, band);
        const a = band === 0 ? Math.max(a0, sec.sCut) : a0;
        const rowCount = (LOD === "game" ? GAME_ROWS : ROWS)[band];
        for (let j = 0; j < rowCount; j++) {
          const tau = j / (rowCount - 1);
          const sv = rowS(sec, band, tau);
          const p = pointAt(sec, sv);
          // tangent along the section (one-sided at the band's ends: creases stay crisp) and along z
          const h = 0.0025;
          let s1 = sv - h, s2 = sv + h;
          if (b - a > 2 * h) { if (s1 < a) { s1 = a; s2 = a + 2 * h; } else if (s2 > b) { s2 = b; s1 = b - 2 * h; } }
          const q1 = pointAt(sec, s1), q2 = pointAt(sec, s2);
          // along z: the nearest points on the neighbouring sections, within the same band (true surface tangent)
          const range = (q: Section): V2 => { const [lo, hi] = bandRange(q, band); return [band === 0 ? Math.max(lo, q.sCut) : lo, hi]; };
          const [pl, ph] = range(prev), [nl, nh] = range(next);
          const pm = closestOn(prev, p[0], p[1], pl, ph), pp = closestOn(next, p[0], p[1], nl, nh);
          const tsx = q2[0] - q1[0], tsy = q2[1] - q1[1];
          const tzx = pp[0] - pm[0], tzy = pp[1] - pm[1], tzz = next.z - prev.z;
          const n = new THREE.Vector3(tsy * tzz, -tsx * tzz, tsx * tzy - tsy * tzx);
          if (n.lengthSq() < 1e-16) n.copy(tipNormal);
          if (Math.abs(p[0]) < 1e-5) {                        // on the mirror plane the normal lies in it (no seam)
            const l2 = n.lengthSq();
            n.x = 0;
            if (n.lengthSq() < 0.05 * l2) n.copy(tipNormal);    // (the nose tip, where the neighbours give no sideways tangent)
          }
          n.normalize();
          row.push(target.vert(p[0], p[1], z, n.x, n.y, n.z, uOf(z), sv / V_SCALE));
          if (band === 0 && Math.abs(z - Z_TAIL_EDGE) < 1e-6) firstRow.push({ x: p[0], y: p[1], s: sv, n: n.clone() });
        }
        ids.push(row);
      }
      target.grid(ids);
    }
  }
  // the tail: a rounded edge (quarter circle) into a flat panel
  const rings: TailRing[][] = [];
  const K = lod(6, 3);
  for (let k = 0; k <= K; k++) {
    const th = (k / K) * Math.PI / 2;
    const ring: TailRing[] = firstRow.map((r) => {
      const n2 = new THREE.Vector2(r.n.x, r.n.y).normalize();
      const inset = TAIL_R * (1 - Math.cos(th));
      const n = r.n.clone().multiplyScalar(Math.cos(th)).add(new THREE.Vector3(0, 0, -Math.sin(th))).normalize();
      return { x: r.x - n2.x * inset, y: r.y - n2.y * inset, z: Z_TAIL_EDGE - TAIL_R * Math.sin(th), n, s: r.s };
    });
    rings.push(ring);
  }
  paint.grid(rings.map((ring) => ring.map((r) => paint.vert(r.x, r.y, r.z, r.n.x, r.n.y, r.n.z, uOf(r.z), r.s / V_SCALE))));
  const last = rings[K];
  const yc = (last[0].y + last[last.length - 1].y) / 2;
  const centre = paint.vert(0, yc, Z_TAIL, 0, 0, -1, 0, yc);
  const fan = [paint.vert(0, last[0].y, Z_TAIL, 0, 0, -1, 0, last[0].y), ...last.map((r) => paint.vert(r.x, r.y, Z_TAIL, 0, 0, -1, r.x, r.y))];
  for (let i = 0; i < fan.length - 1; i++) paint.tri(centre, fan[i], fan[i + 1]);
  return { rings };
}

/** the tail panel's outline: half width at height y */
function tailHalfWidth(rings: TailRing[][], yv: number): number {
  const last = rings[rings.length - 1];
  let best = 0;
  for (let i = 0; i < last.length - 1; i++) {
    const a = last[i], b = last[i + 1];
    if ((a.y - yv) * (b.y - yv) <= 0 && Math.abs(b.y - a.y) > 1e-9) best = Math.max(best, lerp(a.x, b.x, (yv - a.y) / (b.y - a.y)));
  }
  return best;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Underneath: floor, sills, wheel wells, splitter, diffuser
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
const diffuserY = (z: number): number => Y_FLOOR + 0.19 * Math.pow(clamp((Z_DIFFUSER - z) / (Z_DIFFUSER - Z_TAIL), 0, 1), 1.3);

function buildUnderbody(dark: Mesher, floor: Mesher, black: Mesher, rings: TailRing[][]): void {
  const zEnd = Z_NOSE - 0.012;
  const spans: Array<[number, number, boolean]> = [[Z_TAIL, Z_DIFFUSER, false], [Z_DIFFUSER, AXLE_R + ARCH_DZ, true], [AXLE_R + ARCH_DZ, AXLE_F - ARCH_DZ, false], [AXLE_F - ARCH_DZ, AXLE_F + ARCH_DZ, true], [AXLE_F + ARCH_DZ, zEnd, false]];
  const xbAt = (z: number): number => controlPoints(Math.max(z, Z_TAIL_EDGE))[0][0] - (z < Z_TAIL_EDGE ? (Z_TAIL_EDGE - z) * 0.6 : 0);
  const ybAt = (z: number): number => controlPoints(Math.max(z, Z_TAIL_EDGE))[0][1];
  for (const [z0, z1, arch] of spans) {
    const n = Math.max(2, Math.ceil((z1 - z0) / (z1 > 1.7 ? lod(0.015, 0.045) : lod(0.05, 0.15))));
    const plate: number[][] = [], wall: number[][] = [];
    for (let i = 0; i <= n; i++) {
      const z = lerp(z0, z1, i / n);
      const floorY = z < Z_DIFFUSER ? diffuserY(z) : Y_FLOOR;
      const xe = arch ? TUB : xbAt(z) - 0.004;
      const nz = z < Z_DIFFUSER ? -0.35 : 0;
      plate.push([floor.vert(0, floorY, z, 0, -1, nz, 0.5, uOf(z)), floor.vert(xe, floorY, z, 0, -1, nz, 0.5 + xe / 2.1, uOf(z))]);
      if (!arch) {
        const dz = 0.01, slope = (xbAt(z + dz) - xbAt(z - dz)) / (2 * dz);
        wall.push([dark.vert(xe, floorY, z, 1, 0, -slope, 0, 0), dark.vert(xe, ybAt(z) + 0.004, z, 1, 0, -slope, 1, 0)]);
      }
    }
    floor.grid(plate);
    if (!arch) dark.grid(wall);
    if (z1 === zEnd) {
      // close the chin
      const y1 = ybAt(z1) + 0.004, xe = xbAt(z1) - 0.004;
      dark.quad(dark.vert(0, Y_FLOOR, z1, 0, 0, 1), dark.vert(xe, Y_FLOOR, z1, 0, 0, 1), dark.vert(xe, y1, z1, 0, 0, 1), dark.vert(0, y1, z1, 0, 0, 1));
    }
  }
  // rear: the diffuser's back face up to the tail panel
  const capBottom = rings[rings.length - 1][0].y;
  const xr = xbAt(Z_TAIL);
  dark.quad(dark.vert(0, diffuserY(Z_TAIL), Z_TAIL, 0, 0, -1), dark.vert(xr, diffuserY(Z_TAIL), Z_TAIL, 0, 0, -1), dark.vert(xr, capBottom + 0.01, Z_TAIL, 0, 0, -1), dark.vert(0, capBottom + 0.01, Z_TAIL, 0, 0, -1));
  // wheel wells: liner over each wheel (axis along x) and the inner wall
  for (const zw of [AXLE_F, AXLE_R]) {
    const RL = ARCH_R + 0.03;
    const phi0 = Math.asin((Y_FLOOR - WHEEL_Y) / RL);
    const N = lod(30, 10);
    const inner: number[] = [], outer: number[] = [], wallIds: number[] = [];
    const hub = dark.vert(TUB, WHEEL_Y, zw, 1, 0, 0);
    for (let i = 0; i <= N; i++) {
      const phi = lerp(phi0, Math.PI - phi0, i / N);
      const yv = WHEEL_Y + RL * Math.sin(phi), z = zw + RL * Math.cos(phi);
      const xo = Math.max(TUB + 0.05, sideX(z, yv) - 0.01);
      const yIn = Math.min(yv, topXZ(TUB, z).y - 0.02), yOut = Math.min(yv, topXZ(xo, z).y - 0.02);
      inner.push(dark.vert(TUB, yIn, z, 0, -Math.sin(phi), -Math.cos(phi)));
      outer.push(dark.vert(xo, yOut, z, 0, -Math.sin(phi), -Math.cos(phi)));
      wallIds.push(dark.vert(TUB, yIn, z, 1, 0, 0));
    }
    for (let i = 0; i < N; i++) { dark.quad(inner[i], outer[i], outer[i + 1], inner[i + 1]); dark.tri(hub, wallIds[i], wallIds[i + 1]); }
    dark.tri(hub, wallIds[N], wallIds[0]);
  }
  // splitter: a thin gloss blade following the nose, a few cm proud of the chin
  {
    const z0 = AXLE_F + ARCH_DZ - 0.02;
    const N = lod(40, 14);
    const topRows: number[][] = [], bottomRows: number[][] = [], edge: number[][] = [];
    for (let i = 0; i <= N; i++) {
      const z = i <= N / 2 ? lerp(z0, noseZ(0.9), i / (N / 2)) : noseZ(lerp(0.9, 0, (i - N / 2) / (N / 2)));
      const xb = xbAt(z), dz = 0.004;
      const slope = (xbAt(Math.min(Z_NOSE, z + dz)) - xbAt(z - dz)) / (2 * dz);
      const nrm = new THREE.Vector2(1, -slope).normalize();
      const ox = xb + nrm.x * 0.045, oz = z + nrm.y * 0.045;
      const top = Y_FLOOR + 0.022, bottom = Y_FLOOR - 0.004;
      topRows.push([black.vert(Math.max(0, xb - 0.12), top, z, 0, 1, 0), black.vert(ox, top, oz, 0, 1, 0)]);
      bottomRows.push([black.vert(Math.max(0, xb - 0.12), bottom, z, 0, -1, 0), black.vert(ox, bottom, oz, 0, -1, 0)]);
      edge.push([black.vert(ox, top, oz, nrm.x, 0, nrm.y), black.vert(ox, bottom, oz, nrm.x, 0, nrm.y)]);
    }
    black.grid(topRows);
    black.grid(bottomRows);
    black.grid(edge);
  }
  // diffuser fins
  for (const fx of [0, 0.22, 0.62]) {
    const shape = new THREE.Shape();
    shape.moveTo(Z_DIFFUSER + 0.02, Y_FLOOR + 0.002);
    for (let i = 0; i <= 10; i++) { const z = lerp(Z_DIFFUSER + 0.02, Z_TAIL + 0.004, i / 10); shape.lineTo(z, diffuserY(z) + 0.004); }
    shape.lineTo(Z_TAIL + 0.004, Y_FLOOR + 0.11);
    shape.closePath();
    const g = new THREE.ExtrudeGeometry(shape, { depth: 0.012, bevelEnabled: false });
    g.translate(0, 0, -0.006);
    // shape (x, y) = (z, y), extrusion = x: swap axes (a mirror; Mesher.add flips the winding back)
    black.add(g, new THREE.Matrix4().set(0, 0, 1, fx, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1));
    g.dispose();
  }
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Surface details: panel gaps, lights, intakes, pinstripe, decals
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
const shoulderY = (z: number): number => controlPoints(z)[3][1];

function buildDetails(m: {
  gap: Mesher; paint: Mesher; lens: Mesher; white: Mesher; red: Mesher; glow: Mesher; black: Mesher; intake: Mesher; accent: Mesher;
}, rings: TailRing[][]): void {
  const side = (path: V2[]): SP[] => path.map(([z, yv]) => onSide(z, yv));
  const top = (path: V2[]): SP[] => path.map(([xv, z]) => onTop(xv, z));
  const GAP = 0.006, LIFT = 0.0012;
  // door: front edge behind the front arch, top along the shoulder, rear edge ahead of the side intake
  const doorTop: V2[] = [];
  for (let z = 0.87; z >= -0.58; z -= 0.05) doorTop.push([z, shoulderY(z) - 0.016]);
  ribbon(m.gap, side(roundedPath([[0.87, 0.27], ...doorTop, [-0.58, shoulderY(-0.58) - 0.016], [-0.5, 0.27]], 0.05, true)), GAP, LIFT);
  // canopy base (the frame's outer edge)
  const base: SP[] = [];
  const NB = lod(120, 36);
  for (let i = 0; i <= NB; i++) {
    const t = i / NB;
    const z = lerp(CANOPY_BACK + 0.002, CANOPY_FRONT - 0.002, 0.5 - 0.5 * Math.cos(Math.PI * t));
    base.push(onTop(canopyWidth(z) + 0.004, z));
  }
  ribbon(m.gap, base, 0.006, LIFT);
  // bonnet (frunk lid): down both sides of the hood and across just behind the nose
  ribbon(m.gap, top(roundedPath([[0.5, 0.84], [0.5, 1.78], [0.36, 1.93], [0.0, 1.955]], 0.12, false)), GAP, LIFT);
  // engine lid: two lines sweeping from the canopy's tail out to the rear corners (the reference's top view)
  ribbon(m.gap, top(roundedPath([[0, -1.445], [0.1, -1.5], [0.36, -1.66], [0.56, -1.88], [0.62, -2.125]], 0.16, false)), GAP, LIFT);
  // a thin accent pinstripe low on the doors
  ribbon(m.accent, side(roundedPath([[0.84, 0.31], [-0.48, 0.31]], 0.02, false)), 0.009, LIFT * 1.5);
  // side intake ahead of the rear wheel: a dark slanted mouth with three louvres
  {
    const rows: number[][] = [];
    const NZ = lod(14, 5), NY = lod(8, 4);
    for (let i = 0; i <= NZ; i++) {
      const t = i / NZ;
      const row: number[] = [];
      for (let j = 0; j <= NY; j++) {
        const u = j / NY;
        const z = lerp(lerp(-0.6, -0.66, u), lerp(-0.86, -0.865, u), t);
        const yv = lerp(0.3, 0.585 - 0.03 * t, u);
        const { p, n } = onSide(z, yv);
        const shade = lerp(0.24, 0.05, Math.pow(u, 0.7)) * (1 - 0.35 * Math.abs(t - 0.5));
        m.intake.color = [shade, shade, shade * 1.05];
        row.push(m.intake.vp(p.addScaledVector(n, 0.0015), n, t, u));
      }
      rows.push(row);
    }
    m.intake.grid(rows);
    for (const yv of [0.37, 0.44, 0.51]) {
      const zf = lerp(-0.6, -0.66, (yv - 0.3) / 0.285) - 0.006;
      ribbon(m.paint, side(roundedPath([[zf, yv], [-0.856, yv + 0.012]], 0.01, false)), 0.012, 0.006, 0, PLAIN_UV);
    }
  }
  // headlights: slim smoked slits wrapping round the nose corners, an LED line and three round projectors inside
  {
    const NZ = lod(24, 6), NY = lod(4, 2);
    const rows: number[][] = [];
    const lensAt = (t: number): { z: number; y0: number; y1: number } => {
      const z = lerp(1.82, 2.135, t);
      const yTop = Math.min(crestY(z) - 0.028, lerp(0.585, 0.475, Math.pow(t, 1.15)));
      const h = lerp(0.05, 0.026, t);
      return { z, y0: yTop - h, y1: yTop };
    };
    for (let i = 0; i <= NZ; i++) {
      const { z, y0, y1 } = lensAt(i / NZ);
      const row: number[] = [];
      for (let j = 0; j <= NY; j++) {
        const { p, n } = onSide(z, lerp(y0, y1, j / NY));
        row.push(m.lens.vp(p.addScaledVector(n, 0.0025), n, i / NZ, j / NY));
      }
      rows.push(row);
    }
    m.lens.grid(rows);
    const strip: SP[] = [];
    const NS = lod(30, 8);
    for (let i = 0; i <= NS; i++) {
      const { z, y0, y1 } = lensAt(lerp(0.03, 0.97, i / NS));
      strip.push(onSide(z, lerp(y0, y1, 0.8)));
    }
    ribbon(m.white, strip, 0.0065, 0.004);
    const lensDisc = new THREE.CircleGeometry(1, lod(16, 6)), ringDisc = new THREE.RingGeometry(1, 1.45, lod(16, 6));
    for (const t of [0.2, 0.36, 0.52]) {
      const { z, y0, y1 } = lensAt(t);
      const { p, n } = onSide(z, lerp(y0, y1, 0.36));
      const r = lerp(y0, y1, 0.36) - y0;
      const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
      m.white.add(lensDisc, new THREE.Matrix4().compose(p.clone().addScaledVector(n, 0.0042), q, new THREE.Vector3(r * 0.8, r * 0.8, 1)));
      m.black.add(ringDisc, new THREE.Matrix4().compose(p.clone().addScaledVector(n, 0.0038), q, new THREE.Vector3(r * 0.8, r * 0.8, 1)));
    }
    // the mouth: a gloss black band along the bottom of the nose
    const mouth: number[][] = [];
    for (let i = 0; i <= 24; i++) {
      const t = i / 24, z = noseZ(lerp(0.94, 0, t));
      const yb = controlPoints(z)[0][1];
      const h = lerp(0.045, 0.06, t) * smoothstep(0, 0.18, t);
      const row: number[] = [];
      for (let j = 0; j <= 3; j++) {
        const { p, n } = onSide(z, yb - 0.002 + (j / 3) * (h + 0.006));
        row.push(m.black.vp(p.addScaledVector(n, 0.002), n));
      }
      mouth.push(row);
    }
    m.black.grid(mouth);
  }
  // tail: the full-width light bar on the tail panel, wrapping round the corners into short end pieces on the sides
  {
    const yL = 0.758;
    const path: SP[] = [];
    const xMax = tailHalfWidth(rings, yL);
    const NT = lod(24, 8);
    for (let i = 0; i <= NT; i++) path.push({ p: new THREE.Vector3(lerp(0, xMax, i / NT), yL, Z_TAIL), n: new THREE.Vector3(0, 0, -1) });
    for (let k = rings.length - 2; k >= 0; k--) {
      const r = rings[k];
      for (let j = 0; j < r.length - 1; j++) {
        const a = r[j], b = r[j + 1];
        if ((a.y - yL) * (b.y - yL) <= 0 && Math.abs(b.y - a.y) > 1e-9 && a.x > 0.3) {
          const t = (yL - a.y) / (b.y - a.y);
          path.push({ p: new THREE.Vector3(lerp(a.x, b.x, t), yL, lerp(a.z, b.z, t)), n: a.n.clone().lerp(b.n, t).normalize() });
          break;
        }
      }
    }
    for (let i = 1; i <= 6; i++) path.push(onSide(Z_TAIL_EDGE + 0.02 * i, yL));
    const widthAt = (t: number): number => lerp(0.026, 0.052, smoothstep(0.62, 0.86, t));
    ribbon(m.black, path, (t) => widthAt(t) + 0.03, 0.0012);
    ribbon(m.red, path, widthAt, 0.0028);
    ribbon(m.glow, path, (t) => Math.min(widthAt(t) * 2.3, widthAt(t) + 0.026), 0.004);   // stays on the dark housing
    // lower tail: gloss black band above the diffuser
    const y0 = rings[rings.length - 1][0].y, y1 = 0.55;
    const rows: number[][] = [];
    for (let i = 0; i <= 6; i++) {
      const yv = lerp(y0, y1, i / 6);
      const xw = tailHalfWidth(rings, Math.max(yv, y0 + 0.002));
      rows.push([m.black.vert(0, yv, Z_TAIL - 0.0015, 0, 0, -1), m.black.vert(xw, yv, Z_TAIL - 0.0015, 0, 0, -1)]);
    }
    m.black.grid(rows);
  }
}

/** The Solana mark on the bonnet (reading from the chase camera) and on both doors (reading from each side). */
function buildDecals(aspect: number): THREE.BufferGeometry {
  const m = new Mesher();
  const N = lod(12, 6);
  // doors: centred low on the door, laid out by arc length up the side so the mark keeps its proportions
  const dw = 0.46, dh = dw * aspect, zc = 0.1, yc = 0.53;
  const door = (z: number, ds: number): THREE.Vector3 => { const sec = surf(z); const p = pointAt(sec, sideS(sec, yc) + ds); return new THREE.Vector3(p[0], p[1], z); };
  const doorRows: Array<Array<{ p: THREE.Vector3; n: THREE.Vector3; u: number; v: number }>> = [];
  for (let i = 0; i <= N; i++) {
    const u = i / N, z = zc - (u - 0.5) * dw;                // +x door: image right → toward the tail
    const row = [];
    for (let j = 0; j <= N; j++) {
      const v = j / N;
      const { p, n } = surfacePoint(door, z, (v - 0.5) * dh, new THREE.Vector3(1, 0, 0));
      row.push({ p: p.addScaledVector(n, 0.0018), n, u, v });
    }
    doorRows.push(row);
  }
  m.grid(doorRows.map((row) => row.map(({ p, n, u, v }) => m.vp(p, n, u, v))));
  // the −x door: the mirror image in space, with u flipped so the mark is not mirrored
  m.grid(doorRows.map((row) => row.map(({ p, n, u, v }) => m.vert(-p.x, p.y, p.z, -n.x, n.y, n.z, 1 - u, v))));
  // bonnet: image up = forward (+z), image right = the car's right (−x), as the chase camera sees it
  const hw = 0.56, hh = hw * aspect, zc2 = 1.47;
  const along: V2[] = [];
  let acc = 0;
  let prev = topXZ(0, zc2 - 0.6);
  for (let z = zc2 - 0.6; z <= zc2 + 0.6; z += 0.004) { const p = topXZ(0, z); acc += p.distanceTo(prev); prev = p; along.push([acc, z]); }
  const mid = along.reduce((best, a) => (Math.abs(a[1] - zc2) < Math.abs(best[1] - zc2) ? a : best), along[0])[0];
  const zAt = (sv: number): number => {
    for (let i = 1; i < along.length; i++) if (along[i][0] >= sv) return lerp(along[i - 1][1], along[i][1], (sv - along[i - 1][0]) / (along[i][0] - along[i - 1][0] || 1));
    return along[along.length - 1][1];
  };
  const rows: number[][] = [];
  for (let i = 0; i <= N; i++) {
    const u = i / N, d = (0.5 - u) * hw;                    // arc distance from the centre line, + = left (+x)
    const row: number[] = [];
    for (let j = 0; j <= N; j++) {
      const v = j / N;
      const z = zAt(mid + (v - 0.5) * hh);
      const sec = surf(z);
      const pXY = pointAt(sec, sec.total - Math.abs(d));
      const sgn = d < 0 ? -1 : 1;
      const { n } = surfacePoint(topXZ, Math.max(0.001, pXY[0]), z, new THREE.Vector3(0, 1, 0));
      const lift = 0.0018;
      row.push(m.vert(sgn * (pXY[0] + n.x * lift), pXY[1] + n.y * lift, z + n.z * lift, sgn * n.x, n.y, n.z, u, v));
    }
    rows.push(row);
  }
  m.grid(rows);
  return m.geometry();
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Cabin: tub, dash, seats, steering wheel
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
const CABIN_FRONT = 0.7;
const CABIN_BACK = -0.95;

function buildCabin(m: { interior: Mesher; seat: Mesher; bolster: Mesher; accent: Mesher; chrome: Mesher; screen: Mesher }): void {
  const dark = new Mesher();
  const baseY = (z: number): number => topXZ(canopyWidth(z), z).y;
  // shelf under the windshield base and behind the seats, at the level of the canopy base
  for (const [z0, z1] of [[CANOPY_BACK + 0.004, CABIN_BACK], [CABIN_FRONT, CANOPY_FRONT - 0.004]] as Array<[number, number]>) {
    const rows: number[][] = [];
    const NC = lod(18, 6);
    for (let i = 0; i <= NC; i++) {
      const z = lerp(z0, z1, i / NC), cw = canopyWidth(z) + 0.012, yv = baseY(z) - 0.012;
      rows.push([dark.vert(0, yv, z, 0, 1, 0), dark.vert(cw, yv, z, 0, 1, 0)]);
    }
    dark.grid(rows);
  }
  // tub: floor, side walls following the canopy, a ledge up to the frame, bulkhead and dash face
  const rows: number[][] = [], walls: number[][] = [], ledge: number[][] = [];
  const FLOOR = 0.3;
  for (let i = 0; i <= 24; i++) {
    const z = lerp(CABIN_BACK, CABIN_FRONT, i / 24);
    const cw = canopyWidth(z), xw = Math.min(0.74, cw - 0.012), yTop = baseY(z) - 0.008;
    rows.push([dark.vert(0, FLOOR, z, 0, 1, 0), dark.vert(xw, FLOOR, z, 0, 1, 0)]);
    walls.push([dark.vert(xw, FLOOR, z, -1, 0, 0), dark.vert(xw, yTop, z, -1, 0, 0)]);
    ledge.push([dark.vert(xw, yTop, z, 0, 1, 0), dark.vert(cw + 0.01, yTop, z, 0, 1, 0)]);
  }
  dark.grid(rows);
  dark.grid(walls);
  dark.grid(ledge);
  for (const [z, nz] of [[CABIN_BACK, 1], [CABIN_FRONT, -1]] as Array<[number, number]>) {
    const cw = canopyWidth(z), xw = Math.min(0.74, cw - 0.012), yTop = baseY(z) - 0.008;
    dark.quad(dark.vert(0, FLOOR, z, 0, 0, nz), dark.vert(xw, FLOOR, z, 0, 0, nz), dark.vert(xw, yTop, z, 0, 0, nz), dark.vert(0, yTop, z, 0, 0, nz));
  }
  dark.mirrorX();
  m.interior.add(dark.geometry());
  // dash: a soft slab under the windshield with a slim screen
  const mat = new THREE.Matrix4();
  const place = (target: Mesher, g: THREE.BufferGeometry, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0, s = 1): void => {
    mat.compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)), new THREE.Vector3(s, s, s));
    target.add(g, mat);
  };
  const dash = rbox(1.36, 0.12, 0.26, 2, 0.05);
  place(m.interior, dash, 0, 0.7, 0.6, 0.12);
  const screen = rbox(0.34, 0.012, 0.16, 2, 0.005);
  place(m.screen, screen, 0, 0.765, 0.545, 0.55);
  // centre console
  place(m.interior, rbox(0.2, 0.16, 0.98, 2, 0.05), 0, 0.37, 0.0);
  // seats: light leather buckets with dark bolsters and an accent stripe
  for (const sx of [0.355, -0.355]) {
    place(m.seat, rbox(0.44, 0.1, 0.5, 2, 0.04), sx, 0.37, -0.2, -0.1);
    place(m.seat, rbox(0.42, 0.5, 0.1, 2, 0.04), sx, 0.62, -0.53, -0.36);
    place(m.seat, rbox(0.24, 0.12, 0.09, 2, 0.04), sx, 0.895, -0.64, -0.36);
    for (const bx of [-0.19, 0.19]) {
      place(m.bolster, rbox(0.075, 0.44, 0.15, 2, 0.03), sx + bx, 0.63, -0.51, -0.36, 0, bx * -0.6);
      place(m.bolster, rbox(0.075, 0.1, 0.46, 2, 0.03), sx + bx, 0.4, -0.2, -0.1);
    }
    place(m.accent, rbox(0.045, 0.46, 0.102, 2, 0.015), sx, 0.625, -0.528, -0.36);
  }
  // steering wheel on the left (+x): rim, three spokes, column
  const tilt = 0.5;
  const wheelPos = new THREE.Vector3(0.355, 0.7, 0.33);
  place(m.interior, new THREE.TorusGeometry(0.165, 0.018, lod(10, 4), lod(36, 12)), wheelPos.x, wheelPos.y, wheelPos.z, tilt);
  for (const a of [Math.PI / 2, Math.PI * 7 / 6, Math.PI * 11 / 6]) {
    const g = new THREE.BoxGeometry(0.15, 0.022, 0.012);
    g.translate(0.075, 0, 0);
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(tilt, 0, 0)).multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), a));
    m.interior.add(g, new THREE.Matrix4().compose(wheelPos, q, new THREE.Vector3(1, 1, 1)));
  }
  place(m.chrome, new THREE.CylinderGeometry(0.035, 0.035, 0.03, lod(16, 6)), wheelPos.x, wheelPos.y, wheelPos.z, tilt + Math.PI / 2);
  const col = new THREE.CylinderGeometry(0.025, 0.03, 0.26, lod(10, 5));
  place(m.interior, col, wheelPos.x, wheelPos.y + 0.05, wheelPos.z + 0.12, tilt + Math.PI / 2 - 0.2);
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Mirrors, nozzles
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
function buildMirrors(paint: Mesher, black: Mesher, chrome: Mesher): void {
  // a pod: an egg revolved about its length, flat at the back where the glass sits
  const prof: V2[] = [[0, -0.032], [0.6, -0.032], [1, -0.032], [1, -0.032], [0.98, -0.012], [0.9, 0.025], [0.72, 0.06], [0.45, 0.088], [0.18, 0.104], [0, 0.108]];
  const pod = revolveX(prof, 28);
  const glassDisc = new THREE.CircleGeometry(0.9, lod(28, 9));
  for (const s of [1, -1]) {
    const centre = new THREE.Vector3(1.0 * s, 0.905, 0.585);
    const m = new THREE.Matrix4().compose(centre, new THREE.Quaternion().setFromEuler(new THREE.Euler(0, -Math.PI / 2, 0.06 * s)), new THREE.Vector3(1, 1, 1));
    m.multiply(new THREE.Matrix4().makeScale(1, 0.046, 0.072));          // after the rotation: x(len) → z, so scale y and the (rotated) width
    paint.add(pod, m, PLAIN_UV);
    const gm = new THREE.Matrix4().compose(centre.clone().add(new THREE.Vector3(0, 0, -0.0335)), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI, 0)), new THREE.Vector3(0.072, 0.046, 1));
    chrome.add(glassDisc, gm);
    // stalk from the door top to the pod
    const a = new THREE.Vector3(0.875 * s, 0.82, 0.62), b = centre.clone().add(new THREE.Vector3(-0.04 * s, -0.012, 0.01));
    const len = a.distanceTo(b);
    const stalk = rbox(0.045, len + 0.02, 0.022, 2, 0.008);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
    black.add(stalk, new THREE.Matrix4().compose(a.clone().add(b).multiplyScalar(0.5), q, new THREE.Vector3(1, 1, 1)));
  }
}

/** a thruster nozzle along +x (bell), outer metal and the glowing inside */
function nozzleGeometry(rIn: number, rOut: number, len: number): { outer: THREE.BufferGeometry; inner: THREE.BufferGeometry } {
  const outer = revolveX([[rIn * 0.9, 0], [rIn, len * 0.15], [lerp(rIn, rOut, 0.55), len * 0.6], [rOut, len * 0.96], [rOut * 1.03, len], [rOut * 1.03, len], [rOut * 0.93, len]], 24);
  const inner = revolveX([[rOut * 0.93, len], [lerp(rIn, rOut, 0.4) * 0.9, len * 0.5], [rIn * 0.75, len * 0.12], [rIn * 0.75, len * 0.12], [0, len * 0.12]], 24);
  return { outer, inner };
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Wheels: low-profile tyres with sidewalls and tread grooves, ten-spoke rims, drilled-look discs, red calipers
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
interface WheelGeo { tyre: THREE.BufferGeometry; rim: THREE.BufferGeometry; chrome: THREE.BufferGeometry; disc: THREE.BufferGeometry; hub: THREE.BufferGeometry; caliper: THREE.BufferGeometry; accent: THREE.BufferGeometry }

function buildWheel(hw: number): WheelGeo {
  // tyre: inner bead → sidewall → shoulder → tread (three grooves) → shoulder → outer sidewall → bead
  const R = WHEEL_R;
  const prof: V2[] = [[0.296, -hw + 0.016], [0.31, -hw + 0.004], [0.334, -hw - 0.003], [0.36, -hw - 0.003], [0.38, -hw + 0.006], [0.393, -hw + 0.02], [R, -hw + 0.042]];
  const grooves = [-0.42, 0.42].map((f) => f * (hw - 0.04));
  if (LOD === "full") for (const g of grooves) {
    prof.push([R, g - 0.009], [R, g - 0.009], [R - 0.007, g - 0.006], [R - 0.007, g - 0.006], [R - 0.007, g + 0.006], [R - 0.007, g + 0.006], [R, g + 0.009], [R, g + 0.009]);
  }
  prof.push([R, hw - 0.042], [0.393, hw - 0.02], [0.38, hw - 0.006], [0.36, hw + 0.003], [0.334, hw + 0.003], [0.312, hw - 0.003], [0.305, hw - 0.006], [0.305, hw - 0.006], [0.3, hw - 0.012], [0.296, hw - 0.018]);
  const tyre = revolveX(LOD === "game" ? [0, 2, 4, 6, 7, 9, 11, 13, 14, 16].map((i) => prof[i]) : prof, 48);
  const face = hw - 0.012;
  // rim barrel (inside, seen between the spokes) and the mounting face behind
  const rimM = new Mesher();
  const chromeM = new Mesher();
  rimM.add(revolveX([[0.287, face - 0.012], [0.287, -hw + 0.03], [0.287, -hw + 0.03], [0.06, -hw + 0.03]], 48));
  // ten spokes in five pairs, dished: the hub sits deep, the spoke tips reach the lip; a slight turbine sweep
  const SPOKES = 5;
  for (let k = 0; k < SPOKES; k++) {
    for (const side of [-1, 1]) {
      const K = lod(7, 2);
      const ring: THREE.Vector3[][] = [];
      for (let i = 0; i <= K; i++) {
        const t = i / K;
        const r = lerp(0.072, 0.284, t);
        const ang = (k / SPOKES) * Math.PI * 2 + side * lerp(0.045, 0.13, t) + 0.16 * t * t;
        const wFace = lerp(0.044, 0.028, t), wBack = wFace * 0.6;
        const xf = lerp(face - 0.05, face - 0.004, Math.pow(t, 0.75)), xb = xf - lerp(0.04, 0.026, t);
        const dir = new THREE.Vector3(0, Math.cos(ang), Math.sin(ang)), perp = new THREE.Vector3(0, -Math.sin(ang), Math.cos(ang));
        const at = (x: number, w: number): THREE.Vector3 => dir.clone().multiplyScalar(r).addScaledVector(perp, w / 2).setX(x);
        ring.push([at(xf, wFace), at(xf, -wFace), at(xb, -wBack), at(xb, wBack)]);
      }
      for (let f = 0; f < 4; f++) {
        const rows: number[][] = [];
        for (let i = 0; i <= K; i++) {
          const a = ring[i][f], b = ring[i][(f + 1) % 4];
          const along = (i < K ? ring[i + 1][f] : ring[i][f]).clone().sub(i < K ? ring[i][f] : ring[i - 1][f]);
          const nrm = new THREE.Vector3().crossVectors(b.clone().sub(a), along).normalize();
          const centre = ring[i][0].clone().add(ring[i][2]).multiplyScalar(0.5);
          if (nrm.dot(a.clone().add(b).multiplyScalar(0.5).sub(centre)) < 0) nrm.negate();
          const target = f === 0 ? chromeM : rimM;          // the spoke faces are machined bright
          rows.push([target.vp(a, nrm), target.vp(b, nrm)]);
        }
        (f === 0 ? chromeM : rimM).grid(rows);
      }
    }
  }
  // hub with a centre cap
  rimM.add(revolveX([[0.085, face - 0.06], [0.082, face - 0.05], [0.07, face - 0.044], [0.04, face - 0.04], [0.04, face - 0.04], [0.03, face - 0.034]], 32));
  const rim = rimM.geometry();
  // chrome: the polished lip and five lug nuts
  chromeM.add(revolveX([[0.306, face - 0.004], [0.304, face + 0.002], [0.298, face + 0.006], [0.29, face + 0.004], [0.285, face - 0.004], [0.284, face - 0.014]], 48));
  for (let k = 0; k < lod(5, 0); k++) {                          // (lug nuts: invisible from the chase camera)
    const a = (k / 5) * Math.PI * 2 + Math.PI / 5;
    const nut = new THREE.CylinderGeometry(0.0105, 0.0105, 0.016, 6);
    chromeM.add(nut, new THREE.Matrix4().compose(new THREE.Vector3(face - 0.04, Math.cos(a) * 0.055, Math.sin(a) * 0.055), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, Math.PI / 2)), new THREE.Vector3(1, 1, 1)));
  }
  const chrome = chromeM.geometry();
  const accentM = new Mesher();
  accentM.add(revolveX([[0.026, face - 0.032], [0.026, face - 0.032], [0, face - 0.031]], 24));
  // brake disc: outer edge, face, inner edge; a darker hat in the middle
  const disc = revolveX([[0.258, -0.04], [0.258, -0.012], [0.258, -0.012], [0.13, -0.012], [0.13, -0.012], [0.13, -0.04]], 48);
  const hub = revolveX([[0.13, -0.03], [0.075, -0.026], [0.075, -0.026], [0.075, 0.0]], 24);
  // caliper: a block wrapped round the top-rear of the disc
  const cal = new Mesher();
  const cProf: V2[] = [[0.276, -0.058], [0.276, 0.004], [0.276, 0.004], [0.2, 0.004], [0.2, 0.004], [0.2, -0.058], [0.2, -0.058], [0.276, -0.058]];
  const span = 0.82, mid = -0.55;
  cal.add(revolveX(cProf, 10, mid - span / 2, span));
  for (const phi of [mid - span / 2, mid + span / 2]) {
    const c = Math.cos(phi), s = Math.sin(phi);
    const dir = phi < mid ? -1 : 1;
    const nrm = new THREE.Vector3(0, -s * dir, c * dir);
    const ids = [[0.276, -0.058], [0.276, 0.004], [0.2, 0.004], [0.2, -0.058]].map(([r, x]) => cal.vert(x, r * c, r * s, nrm.x, nrm.y, nrm.z));
    cal.quad(ids[0], ids[1], ids[2], ids[3]);
  }
  return { tyre, rim, chrome, disc, hub, caliper: cal.geometry(), accent: accentM.geometry() };
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Thruster flames
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
let flameTexture: THREE.Texture | undefined;
function flameGradient(): THREE.Texture {
  if (flameTexture) return flameTexture;
  const c = document.createElement("canvas");
  c.width = 8;
  c.height = 256;
  const g = c.getContext("2d");
  if (g) {
    const grad = g.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(0.18, "rgba(255,255,255,0.85)");
    grad.addColorStop(0.55, "rgba(255,255,255,0.35)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = grad;
    g.fillRect(0, 0, 8, 256);
  }
  flameTexture = new THREE.CanvasTexture(c);
  return flameTexture;
}

let underTexture: THREE.Texture | undefined;
/** the floor seen in a kickflip: a battery pack of modules with a centre spine and cross members */
function underbodyTexture(): THREE.Texture {
  if (underTexture) return underTexture;
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 1024;
  const g = c.getContext("2d");
  if (g) {
    g.fillStyle = "#2a2d33";
    g.fillRect(0, 0, 256, 1024);
    // the pack: u 0.5 is the centre line, u 1 the sill; v 0 the tail, 1 the nose (canvas top = nose)
    const top = 1024 * (1 - uOf(0.86)), bottom = 1024 * (1 - uOf(-0.84));
    g.fillStyle = "#7d838d";
    g.fillRect(128 + 6, top, 104, bottom - top);
    g.fillRect(0, top, 128 - 6, bottom - top);
    g.strokeStyle = "#30333a";
    g.lineWidth = 3;
    const rows = 8;
    for (let r = 0; r <= rows; r++) { const y = lerp(top, bottom, r / rows); g.beginPath(); g.moveTo(0, y); g.lineTo(256, y); g.stroke(); }
    for (const x of [44, 88, 168, 212]) { g.beginPath(); g.moveTo(x, top); g.lineTo(x, bottom); g.stroke(); }
    g.fillStyle = "#1c1e22";
    g.fillRect(122, top - 30, 12, bottom - top + 60);
    g.fillStyle = "#8a9099";
    for (let r = 0; r < rows; r++) for (const x of [22, 66, 106, 150, 190, 234]) { g.beginPath(); g.arc(x, lerp(top, bottom, (r + 0.5) / rows), 3, 0, Math.PI * 2); g.fill(); }
    // front and rear subframes
    g.fillStyle = "#3d4148";
    g.fillRect(30, 1024 * (1 - uOf(1.9)), 196, 70);
    g.fillRect(30, 1024 * (1 - uOf(-1.95)), 196, 60);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  underTexture = t;
  return t;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Environment for reflections: a soft outdoor studio (bright sky with soft boxes, a crisp horizon, dark ground),
// so the clear coat and the glass read as glossy even with only a hemisphere + sun in the scene
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
let envTexture: THREE.Texture | undefined;
function studioEnvironment(): THREE.Texture {
  if (envTexture) return envTexture;
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 512;
  const g = c.getContext("2d");
  if (g) {
    const sky = g.createLinearGradient(0, 0, 0, 256);
    sky.addColorStop(0, "#3f4a5e");
    sky.addColorStop(0.65, "#7d8ca6");
    sky.addColorStop(1, "#d9e3f0");
    g.fillStyle = sky;
    g.fillRect(0, 0, 1024, 256);
    const ground = g.createLinearGradient(0, 256, 0, 512);
    ground.addColorStop(0, "#8d939d");
    ground.addColorStop(0.06, "#4f535b");
    ground.addColorStop(1, "#222328");
    g.fillStyle = ground;
    g.fillRect(0, 256, 1024, 256);
    const glowTop = g.createRadialGradient(512, 0, 10, 512, 0, 300);
    glowTop.addColorStop(0, "rgba(220,230,245,0.55)");
    glowTop.addColorStop(1, "rgba(220,230,245,0)");
    g.fillStyle = glowTop;
    g.fillRect(0, 0, 1024, 140);
    g.filter = "blur(4px)";
    g.fillStyle = "#ffffff";
    // soft boxes at mid elevation: ahead (u 0.75), behind (u 0.25), both sides; the chase camera sees the one ahead
    g.fillRect(650, 100, 236, 58);
    g.fillRect(150, 104, 190, 50);
    g.fillRect(440, 140, 70, 74);
    g.fillRect(940, 140, 70, 74);
    g.fillRect(8, 140, 40, 74);
    g.filter = "none";
    g.fillStyle = "rgba(255,255,255,0.95)";
    g.fillRect(0, 251, 1024, 4);
  }
  const t = new THREE.CanvasTexture(c);
  t.mapping = THREE.EquirectangularReflectionMapping;
  t.colorSpace = THREE.SRGBColorSpace;
  envTexture = t;
  return t;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Paint patterns (u along the car, rear → nose; v up the side to the centre line, the same on both sides)
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
function patternTexture(pattern: NonNullable<Skin["pattern"]>, skin: Skin): THREE.Texture {
  const canvas = document.createElement("canvas");
  const size = pattern === "carbon" ? [128, 128] : [1024, 512];
  canvas.width = size[0];
  canvas.height = size[1];
  const ctx = canvas.getContext("2d");
  const Wd = size[0], Ht = size[1];
  if (ctx) {
    if (pattern === "solana") {
      const g = ctx.createLinearGradient(0, Ht, Wd, 0);
      g.addColorStop(0, "#14f195"); g.addColorStop(0.5, "#7a7cf0"); g.addColorStop(1, "#9945ff");
      ctx.fillStyle = g; ctx.fillRect(0, 0, Wd, Ht);
    } else if (pattern === "holo") {
      const g = ctx.createLinearGradient(0, 0, Wd, Ht);
      ["#ffb3f0", "#b3c7ff", "#b3fff0", "#fff3b3", "#ffb3f0", "#c9b8ff"].forEach((c, i, a) => g.addColorStop(i / (a.length - 1), c));
      ctx.fillStyle = g; ctx.fillRect(0, 0, Wd, Ht);
    } else if (pattern === "excel") {
      ctx.fillStyle = skin.body; ctx.fillRect(0, 0, Wd, Ht);
      ctx.strokeStyle = "rgba(255,255,255,0.7)"; ctx.lineWidth = 3;
      for (let x = 0; x <= Wd; x += 96) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, Ht); ctx.stroke(); }
      for (let y = 0; y <= Ht; y += 44) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(Wd, y); ctx.stroke(); }
    } else if (pattern === "carbon") {
      // 2x2 twill
      for (let y = 0; y < 128; y += 16) for (let x = 0; x < 128; x += 16) {
        const phase = ((x + y) / 16) % 4 < 2;
        const g = phase ? ctx.createLinearGradient(x, y, x + 16, y) : ctx.createLinearGradient(x, y, x, y + 16);
        g.addColorStop(0, "#1b1d22"); g.addColorStop(0.5, "#3a3d46"); g.addColorStop(1, "#1b1d22");
        ctx.fillStyle = g; ctx.fillRect(x, y, 16, 16);
      }
    } else if (pattern === "flames") {
      // hot-rod flames: they burn out of the nose and lick back along the sides and over the bonnet, black between
      // (canvas x = along the car, tail → nose; canvas y = 0 at the centre line, bottom = the sill)
      ctx.fillStyle = skin.body; ctx.fillRect(0, 0, Wd, Ht);
      const tongue = (y: number, len: number, thick: number, wave: number): void => {
        const g = ctx.createLinearGradient(Wd, 0, Wd - len, 0);
        g.addColorStop(0, "#fff27a"); g.addColorStop(0.3, "#ffb000"); g.addColorStop(0.7, "#ff4d00"); g.addColorStop(1, "#c81400");
        ctx.fillStyle = g;
        ctx.strokeStyle = "#ff8a00";
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(Wd + 4, y - thick);
        ctx.bezierCurveTo(Wd - len * 0.35, y - thick * 1.15 + wave, Wd - len * 0.72, y - thick * 0.25 - wave, Wd - len, y + wave * 0.3);
        ctx.bezierCurveTo(Wd - len * 0.7, y + thick * 0.25 + wave, Wd - len * 0.36, y + thick * 0.95 - wave, Wd + 4, y + thick);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      };
      // sides (v ≈ 0.18 … 0.48)
      [[284, 0.5, 15, 10], [318, 0.62, 17, -12], [352, 0.46, 16, 9], [386, 0.56, 15, -8], [418, 0.38, 13, 6]].forEach(([y, l, t, w]) => tongue(y, l * Wd, t, w));
      // bonnet and fender tops (v ≈ 0.55 … 0.9)
      [[70, 0.2, 18, 6], [118, 0.25, 19, -7], [168, 0.22, 18, 6], [214, 0.3, 17, -6]].forEach(([y, l, t, w]) => tongue(y, l * Wd, t, w));
      const nose = ctx.createLinearGradient(Wd, 0, Wd - 70, 0);
      nose.addColorStop(0, "#fff27a"); nose.addColorStop(1, "rgba(255,176,0,0)");
      ctx.fillStyle = nose;
      ctx.fillRect(Wd - 70, 0, 70, Ht);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 4;
  if (pattern === "carbon") texture.repeat.set(14, 5);
  return texture;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Geometry cache: built once, shared by every roadster (the game, the shop previews)
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
interface CarGeometry {
  detail: RoadsterDetail;
  parts: Record<string, THREE.BufferGeometry>;
  wheels: { front: WheelGeo; back: WheelGeo };
  flame: { sheath: THREE.BufferGeometry; core: THREE.BufferGeometry; disc: THREE.BufferGeometry };
  decals: Map<number, THREE.BufferGeometry>;
  /** detail "game": the parts merged per material (built once, shared) */
  game?: GameGeometry;
}
const cachedBy: Partial<Record<RoadsterDetail, CarGeometry>> = {};

/** runs a build at a detail level (the tessellation counts read LOD) */
function atDetail<T>(detail: RoadsterDetail, make: () => T): T {
  const outer = LOD;
  LOD = detail;
  try { return make(); } finally { LOD = outer; }
}

function carGeometry(detail: RoadsterDetail = "full"): CarGeometry {
  const hit = cachedBy[detail];
  if (hit) return hit;
  const built = atDetail(detail, buildCarGeometry);
  if (detail === "game") built.game = buildGameGeometry(built);
  cachedBy[detail] = built;
  sectionCache.clear();
  return built;
}

function buildCarGeometry(): CarGeometry {
  const mesher = (): Mesher => new Mesher();
  const m = {
    paint: mesher(), glass: mesher(), black: mesher(), dark: mesher(), floor: mesher(), interior: mesher(), seat: mesher(), bolster: mesher(), accent: mesher(),
    chrome: mesher(), lens: mesher(), white: mesher(), red: mesher(), glow: mesher(), gap: mesher(), intake: mesher(), screen: mesher(), nozzle: mesher()
  };
  // mirrored halves: shell, underbody, details
  const half = { paint: mesher(), glass: mesher(), dark: mesher(), floor: mesher(), black: mesher(), gap: mesher(), lens: mesher(), white: mesher(), red: mesher(), glow: mesher(), intake: mesher(), accent: mesher() };
  const { rings } = buildShell(half.paint, half.glass);
  buildUnderbody(half.dark, half.floor, half.black, rings);
  buildDetails({ gap: half.gap, paint: half.paint, lens: half.lens, white: half.white, red: half.red, glow: half.glow, black: half.black, intake: half.intake, accent: half.accent }, rings);
  for (const k of Object.keys(half) as Array<keyof typeof half>) {
    half[k].mirrorX();
    m[k].add(half[k].geometry(k === "intake"));
  }
  // one-sided extras
  {
    // charge-port flap on the left rear haunch
    const flap = roundedPath([[-1.83, 0.665], [-1.99, 0.665], [-1.99, 0.745], [-1.83, 0.745]], 0.018, true);
    ribbon(m.gap, flap.map(([z, yv]) => onSide(z, yv)), 0.005, 0.0012);
  }
  buildCabin({ interior: m.interior, seat: m.seat, bolster: m.bolster, accent: m.accent, chrome: m.chrome, screen: m.screen });
  buildMirrors(m.paint, m.black, m.chrome);
  // thruster nozzles under the floor and the two boosters in the diffuser
  const nozzle = nozzleGeometry(0.07, 0.1, 0.07);
  const booster = nozzleGeometry(0.05, 0.07, 0.1);
  for (const [x, z] of JET_SPOTS) {
    const mtx = new THREE.Matrix4().makeTranslation(x, Y_FLOOR + 0.005, z).multiply(new THREE.Matrix4().makeRotationZ(-Math.PI / 2));
    m.dark.add(nozzle.outer, mtx);
    m.nozzle.add(nozzle.inner, mtx);
  }
  for (const x of BOOSTER_X) {
    const mtx = new THREE.Matrix4().makeTranslation(x, BOOSTER_Y, Z_TAIL + 0.055).multiply(new THREE.Matrix4().makeRotationY(Math.PI / 2));
    m.black.add(booster.outer, mtx);
    m.nozzle.add(booster.inner, mtx);
  }
  const parts: Record<string, THREE.BufferGeometry> = {};
  for (const k of Object.keys(m) as Array<keyof typeof m>) if (m[k].idx.length) parts[k] = m[k].geometry(k === "intake");
  // flames: open cones from the nozzle (origin) out along −y
  const sheath = new THREE.CylinderGeometry(0.1, 0.25, 1.2, 18, 1, true);
  sheath.translate(0, -0.6, 0);
  const core = new THREE.CylinderGeometry(0.055, 0.09, 0.85, 14, 1, true);
  core.translate(0, -0.425, 0);
  const disc = new THREE.CircleGeometry(0.11, 18);
  disc.rotateX(Math.PI / 2);
  const built: CarGeometry = {
    detail: LOD,
    parts,
    wheels: { front: buildWheel(TYRE_HALF.front), back: buildWheel(TYRE_HALF.back) },
    flame: { sheath, core, disc },
    decals: new Map()
  };
  sectionCache.clear();
  return built;
}

const JET_SPOTS: V2[] = [[0.5, 0.86], [-0.5, 0.86], [0.5, -0.84], [-0.5, -0.84]];
const BOOSTER_X = [-0.42, 0.42];
const BOOSTER_Y = 0.27;

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Materials
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
const FINISH: Record<string, { metalness: number; roughness: number }> = {
  pearl: { metalness: 0.05, roughness: 0.3 },
  midnight: { metalness: 0.55, roughness: 0.32 },
  red: { metalness: 0.25, roughness: 0.3 },
  blue: { metalness: 0.4, roughness: 0.3 },
  gold: { metalness: 0.85, roughness: 0.26 },
  carbon: { metalness: 0.2, roughness: 0.35 }
};

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Detail "game": the coarse parts merged per material — paint (+ gloss black, lenses), trim, glass, gaps + halo, one
// mesh per wheel, one per thruster plume — and a low-poly hull for the sun's shadow. From the chase camera it reads
// the same as the full car: the same shapes and finishes, only fewer triangles and draw calls.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
interface GameGeometry {
  /** paint + gloss black + lenses: paint vertices white (the skin colours them), the rest keep colour/roughness/metal */
  body: THREE.BufferGeometry;
  /** every other opaque part AND the four wheels: per-vertex colour, roughness, metalness, environment share,
   *  self-light, accent/floor masks; wheel vertices (fx.w = 1…4) are placed by their wheel's matrix in the vertex shader */
  trim: THREE.BufferGeometry;
  glass: THREE.BufferGeometry;
  /** panel gaps (dark, alpha) + the light bar's halo (additive): one mesh, see fxMaterial */
  fx: THREE.BufferGeometry;
  /** a thruster plume: sheath + core + nozzle disc, opacity in the vertex alpha (one draw) */
  flame: THREE.BufferGeometry;
}

interface Finish {
  color?: THREE.ColorRepresentation;
  r?: number;
  m?: number;
  /** share of the environment light (the full car's envMapIntensity) */
  env?: number;
  /** MeshBasic in the full car: the colour as is */
  unlit?: boolean;
  /** body only: this vertex keeps its own roughness/metalness instead of the paint's */
  own?: boolean;
  emis?: THREE.Color;
  /** takes the skin's accent colour (and its 0.25 self-light) */
  accent?: boolean;
  /** the underbody texture, self-lit 0.3 */
  floor?: boolean;
  /** the brake caliper: held still while its wheel spins (vertex shader) */
  still?: boolean;
  /** DoubleSide in the full car: the triangles are added twice, the second copy flipped */
  double?: boolean;
}

const lin = (c: THREE.ColorRepresentation): THREE.Color => new THREE.Color(c);
const DARK: Finish = { color: 0x17181c, r: 0.85, m: 0.15, env: 0.4, double: true };
const CHROME: Finish = { color: 0xe4e8ef, r: 0.12, m: 1 };
const ACCENT: Finish = { accent: true, r: 0.35, m: 0.3 };
const TRIM_FINISH: Record<string, Finish> = {
  dark: DARK,
  floor: { color: 0xffffff, r: 0.75, m: 0.3, env: 0.6, double: true, floor: true },
  interior: { color: 0x24262c, r: 0.7, m: 0.05, env: 0.5, double: true },
  seat: { color: 0xe9e5dc, r: 0.55, m: 0, env: 0.6 },
  bolster: { color: 0x1c1d22, r: 0.6, m: 0, env: 0.6 },
  accent: ACCENT,
  chrome: CHROME,
  white: { color: lin(0xf2f8ff).multiplyScalar(2.4), unlit: true },
  red: { color: lin(0xff0012).multiplyScalar(1.15), unlit: true },
  intake: { color: 0xffffff, r: 0.55, m: 0.2, env: 0.5 },
  screen: { color: 0x1a3550, unlit: true },
  nozzle: { color: 0x1a1f26, r: 0.35, m: 0.85, emis: lin(0x2a8fd0).multiplyScalar(0.28) }
};
const BODY_FINISH: Record<string, Finish> = {
  paint: { color: 0xffffff },
  black: { color: 0x0b0c10, r: 0.22, m: 0.2, own: true },
  lens: { color: 0x07080b, r: 0.06, m: 0.3, own: true }
};
const WHEEL_FINISH: Record<keyof WheelGeo, Finish> = {
  tyre: { color: 0x18191c, r: 0.82, m: 0, env: 0.35 },
  rim: { color: 0x41454d, r: 0.3, m: 0.9 },
  chrome: CHROME,
  disc: { color: 0x8e9299, r: 0.42, m: 0.9 },
  hub: DARK,
  accent: ACCENT,
  caliper: { color: 0xd3172a, r: 0.3, m: 0.1, still: true }
};

/** Collects parts into one indexed buffer with the per-vertex finish attributes (pbr, emis, fx). */
class Merge {
  readonly pos: number[] = [];
  readonly nor: number[] = [];
  readonly uv: number[] = [];
  readonly col: number[] = [];
  readonly pbr: number[] = [];
  readonly emis: number[] = [];
  readonly fx: number[] = [];
  readonly idx: number[] = [];

  add(g: THREE.BufferGeometry, f: Finish, m?: THREE.Matrix4, wheel = 0): this {
    const P = g.getAttribute("position"), N = g.getAttribute("normal"), U = g.getAttribute("uv"), C = g.getAttribute("color");
    const I = g.getIndex();
    const nm = m ? new THREE.Matrix3().getNormalMatrix(m) : undefined;
    const flip = m ? m.determinant() < 0 : false;
    const c = lin(f.color ?? 0xffffff);
    const v = new THREE.Vector3(), n = new THREE.Vector3();
    for (const side of f.double ? [1, -1] : [1]) {
      const start = this.pos.length / 3;
      for (let i = 0; i < P.count; i++) {
        v.fromBufferAttribute(P, i);
        if (m) v.applyMatrix4(m);
        if (N) n.fromBufferAttribute(N, i); else n.set(0, 1, 0);
        if (nm) n.applyMatrix3(nm).normalize();
        this.pos.push(v.x, v.y, v.z);
        this.nor.push(n.x * side, n.y * side, n.z * side);
        this.uv.push(U ? U.getX(i) : 0, U ? U.getY(i) : 0);
        this.col.push(c.r * (C ? C.getX(i) : 1), c.g * (C ? C.getY(i) : 1), c.b * (C ? C.getZ(i) : 1));
        this.pbr.push(f.r ?? 1, f.m ?? 0, f.env ?? 1, f.unlit || f.own ? 1 : 0);
        this.emis.push(f.emis?.r ?? 0, f.emis?.g ?? 0, f.emis?.b ?? 0);
        this.fx.push(f.accent ? 1 : 0, f.floor ? 1 : 0, f.still ? 1 : 0, wheel);
      }
      const count = I ? I.count : P.count;
      for (let t = 0; t < count; t += 3) {
        const a = start + (I ? I.getX(t) : t), b = start + (I ? I.getX(t + 1) : t + 1), d = start + (I ? I.getX(t + 2) : t + 2);
        if ((side < 0) !== flip) this.idx.push(a, d, b); else this.idx.push(a, b, d);
      }
    }
    return this;
  }

  geometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.col, 3));
    g.setAttribute("pbr", new THREE.Float32BufferAttribute(this.pbr, 4));
    g.setAttribute("emis", new THREE.Float32BufferAttribute(this.emis, 3));
    g.setAttribute("fx", new THREE.Float32BufferAttribute(this.fx, 4));
    g.setIndex(this.idx);
    g.computeBoundingSphere();
    return g;
  }
}

function buildGameGeometry(geo: CarGeometry): GameGeometry {
  const body = new Merge(), trim = new Merge();
  for (const [key, g] of Object.entries(geo.parts)) {
    if (BODY_FINISH[key]) body.add(g, BODY_FINISH[key]);
    else if (TRIM_FINISH[key]) trim.add(g, TRIM_FINISH[key]);
  }
  // gaps (black, 0.55 over) and the light bar's halo (additive red × 0.3) in one blend: out = rgb · scale + dst · (1 − a),
  // the scale applied after tone mapping, where the full car's blend applied each material's opacity
  const fxPos: number[] = [], fxCol: number[] = [], fxScale: number[] = [], fxIdx: number[] = [];
  const addFx = (g: THREE.BufferGeometry, rgba: number[], scale: number, m?: THREE.Matrix4, both = false): void => {
    const P = g.getAttribute("position"), I = g.getIndex(), start = fxPos.length / 3, v = new THREE.Vector3();
    for (let i = 0; i < P.count; i++) {
      v.fromBufferAttribute(P, i);
      if (m) v.applyMatrix4(m);
      fxPos.push(v.x, v.y, v.z);
      fxCol.push(...rgba);
      fxScale.push(scale);
    }
    const count = I ? I.count : P.count;
    for (let t = 0; t < count; t += 3) {
      const a = start + (I ? I.getX(t) : t), b = start + (I ? I.getX(t + 1) : t + 1), c = start + (I ? I.getX(t + 2) : t + 2);
      fxIdx.push(a, b, c);
      if (both) fxIdx.push(a, c, b);
    }
  };
  if (geo.parts.gap) addFx(geo.parts.gap, [0, 0, 0, 0.55], 0.55);
  const halo = lin(0xff0a24);
  if (geo.parts.glow) addFx(geo.parts.glow, [halo.r, halo.g, halo.b, 0], 0.3);
  const fx = new THREE.BufferGeometry();
  fx.setAttribute("position", new THREE.Float32BufferAttribute(fxPos, 3));
  fx.setAttribute("color", new THREE.Float32BufferAttribute(fxCol, 4));
  fx.setAttribute("fxScale", new THREE.Float32BufferAttribute(fxScale, 1));
  fx.setIndex(fxIdx);
  fx.computeBoundingSphere();
  // wheels, into the trim in their own frame (fx.w = 1…4): the right-hand ones turned outer face out (the full car's
  // spinning.rotation.y = π), their calipers mirrored (the full car's caliper.scale.x = −1)
  const turn = new THREE.Matrix4().makeRotationY(Math.PI), mirror = new THREE.Matrix4().makeScale(-1, 1, 1);
  WHEELS.forEach((spot, i) => {
    const wg = spot.front ? geo.wheels.front : geo.wheels.back;
    const right = spot.x < 0;
    for (const key of Object.keys(WHEEL_FINISH) as Array<keyof WheelGeo>) {
      trim.add(wg[key], WHEEL_FINISH[key], right ? (key === "caliper" ? mirror : turn) : undefined, i + 1);
    }
  });
  const trimGeometry = trim.geometry();
  // the wheels' vertices sit at the origin until the shader places them: bound the whole car, folded wheels included
  trimGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0.55, 0), Math.hypot(Z_NOSE, 1.3) + 0.1);
  // a plume: colour × the game's lift (GameScene.prepareCar lifts the sheath ×2.2 once; baked here, the material is
  // flagged hdr so it is not lifted again), opacity in the vertex alpha (the blend applies it after tone mapping)
  const plume = (g: THREE.BufferGeometry, color: number, lift: number, opacity: number, pinUv: boolean): THREE.BufferGeometry => {
    const out = g.clone();
    const c = lin(color).multiplyScalar(lift);
    const n = out.getAttribute("position").count;
    out.setAttribute("color", new THREE.Float32BufferAttribute(Array.from({ length: n * 4 }, (_, i) => [c.r, c.g, c.b, opacity][i % 4]), 4));
    if (pinUv) out.setAttribute("uv", new THREE.Float32BufferAttribute(Array.from({ length: n * 2 }, (_, i) => (i % 2 === 0 ? 0.5 : 1)), 2));   // the gradient's opaque white end
    return out;
  };
  const flame = mergeGeometries([plume(geo.flame.sheath, 0x5cc8ff, 2.2, 0.55, false), plume(geo.flame.core, 0xeafcff, 1, 0.9, false), plume(geo.flame.disc, 0xbff3ff, 1, 0.9, true)], false);
  flame.computeBoundingSphere();
  return { body: body.geometry(), trim: trimGeometry, glass: geo.parts.glass, fx, flame };
}

/**
 * The trim's material: one MeshStandardMaterial reading colour, roughness, metalness, environment share, self-light
 * and the accent / floor masks per vertex (attributes pbr, emis, fx). Wheel vertices (fx.w = 1…4) are moved by
 * uWheel[i] (their wheel group × fold pad, body space) — rigid skinning, so the four wheels cost no draw call of their
 * own — and `fx.z` = 1 turns a vertex back by its wheel's spin about x first (the brake caliper holds still). Not a
 * MeshPhysicalMaterial on purpose: GameScene's cheap-car swap replaces physical materials, this one stays as it is.
 */
interface TrimUniforms { uAccent: { value: THREE.Color }; uFloor: { value: THREE.Texture }; uWheel: { value: THREE.Matrix4[] }; uSpin: { value: THREE.Vector4 } }
function trimMaterial(env: THREE.Texture, accent: THREE.Color): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, metalness: 0, envMap: env });
  const uniforms: TrimUniforms = {
    uAccent: { value: accent }, uFloor: { value: underbodyTexture() },
    uWheel: { value: [0, 1, 2, 3].map(() => new THREE.Matrix4()) }, uSpin: { value: new THREE.Vector4() }
  };
  m.userData.trim = uniforms;
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>
attribute vec4 pbr;
attribute vec3 emis;
attribute vec4 fx;
uniform mat4 uWheel[4];
uniform vec4 uSpin;
varying vec4 vTrimPbr;
varying vec3 vTrimEmis;
varying vec3 vTrimFx;
varying vec2 vTrimUv;
vec3 trimHold(vec3 p, float a) { float c = cos(a), s = sin(a); return vec3(p.x, c * p.y + s * p.z, c * p.z - s * p.y); }`)
      .replace("#include <beginnormal_vertex>", `#include <beginnormal_vertex>
\tint trimWheel = int(fx.w + 0.5) - 1;
\tif (trimWheel >= 0) {
\t\tif (fx.z > 0.5) objectNormal = trimHold(objectNormal, uSpin[trimWheel]);
\t\tobjectNormal = mat3(uWheel[trimWheel]) * objectNormal;
\t}`)
      .replace("#include <begin_vertex>", `#include <begin_vertex>
\tif (trimWheel >= 0) {
\t\tif (fx.z > 0.5) transformed = trimHold(transformed, uSpin[trimWheel]);
\t\ttransformed = (uWheel[trimWheel] * vec4(transformed, 1.0)).xyz;
\t}
\tvTrimPbr = pbr; vTrimEmis = emis; vTrimFx = fx.xyz; vTrimUv = uv;`);
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>
uniform vec3 uAccent;
uniform sampler2D uFloor;
varying vec4 vTrimPbr;
varying vec3 vTrimEmis;
varying vec3 vTrimFx;
varying vec2 vTrimUv;`)
      .replace("#include <envmap_physical_pars_fragment>", THREE.ShaderChunk.envmap_physical_pars_fragment.replace(/envMapIntensity/g, "(envMapIntensity * vTrimPbr.z)"))
      .replace("#include <color_fragment>", `#include <color_fragment>
\tdiffuseColor.rgb = mix(diffuseColor.rgb, uAccent, vTrimFx.x);
\tif (vTrimFx.y > 0.5) diffuseColor.rgb *= texture2D(uFloor, vTrimUv).rgb;`)
      .replace("#include <roughnessmap_fragment>", "float roughnessFactor = vTrimPbr.x;")
      .replace("#include <metalnessmap_fragment>", "float metalnessFactor = vTrimPbr.y;")
      .replace("#include <emissivemap_fragment>", "#include <emissivemap_fragment>\n\ttotalEmissiveRadiance += vTrimEmis + uAccent * (0.25 * vTrimFx.x) + diffuseColor.rgb * (0.3 * vTrimFx.y);")
      .replace("#include <opaque_fragment>", "outgoingLight = mix(outgoingLight, diffuseColor.rgb, vTrimPbr.w);\n#include <opaque_fragment>");
  };
  m.customProgramCacheKey = () => "roadster-trim";
  return m;
}

/** Gaps + halo: out = rgb · fxScale + dst · (1 − a); fxScale is applied after tone mapping and fog, exactly where the
 *  full car's blend applied each material's opacity. */
function fxMaterial(): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({
    vertexColors: true, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
    blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor
  });
  m.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nattribute float fxScale;\nvarying float vFxScale;")
      .replace("#include <begin_vertex>", "#include <begin_vertex>\n\tvFxScale = fxScale;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\nvarying float vFxScale;")
      .replace("#include <dithering_fragment>", "gl_FragColor.rgb *= vFxScale;\n#include <dithering_fragment>");
  };
  m.customProgramCacheKey = () => "roadster-fx";
  return m;
}

/** The paint in detail "game": its gloss black and lenses keep their own roughness and metalness (attribute pbr.w). */
function bodyPaintShader(paint: THREE.MeshPhysicalMaterial): void {
  paint.vertexColors = true;
  paint.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nattribute vec4 pbr;\nvarying vec4 vBodyPbr;")
      .replace("#include <begin_vertex>", "#include <begin_vertex>\n\tvBodyPbr = pbr;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\nvarying vec4 vBodyPbr;")
      .replace("#include <roughnessmap_fragment>", "#include <roughnessmap_fragment>\n\troughnessFactor = mix(roughnessFactor, vBodyPbr.x, vBodyPbr.w);")
      .replace("#include <metalnessmap_fragment>", "#include <metalnessmap_fragment>\n\tmetalnessFactor = mix(metalnessFactor, vBodyPbr.y, vBodyPbr.w);");
  };
  paint.customProgramCacheKey = () => "roadster-body";
}

/** Writes nothing (no colour, no depth): the shadow proxy only shows in the sun's shadow map. */
let proxyMat: THREE.MeshBasicMaterial | undefined;
const proxyMaterial = (): THREE.MeshBasicMaterial => (proxyMat ??= new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false, side: THREE.DoubleSide }));

/** The shadow hull: 12 stations of the analytic body section (16 points round, floor closed, following the diffuser),
 *  the diffuser fins, a hexagonal prism per wheel. ~490 triangles. */
let hullGeometry: THREE.BufferGeometry | undefined;
function shadowHull(): THREE.BufferGeometry {
  if (hullGeometry) return hullGeometry;
  const m = new Mesher();
  // the first ring is the tail panel: the tail edge's section pulled in by the rounded edge's radius
  const zs = [Z_TAIL, Z_TAIL_EDGE, -1.95, -1.6, -1.2, -0.7, -0.1, 0.5, 1.05, 1.5, 1.85, 2.12];
  const S = 6;
  const rings: number[][] = [];
  const loops: V2[][] = [];
  for (const z of zs) {
    const sec = sectionAt(Math.max(z, Z_TAIL_EDGE), null);
    const half: V2[] = [];
    for (let i = 0; i <= S; i++) half.push(pointAt(sec, (sec.total * i) / S));
    const fy = z < Z_DIFFUSER ? diffuserY(z) : Y_FLOOR;
    if (z < Z_TAIL_EDGE) for (const p of half) { p[0] = Math.max(0, p[0] - TAIL_R); p[1] = fy + (p[1] - fy) * 0.93; }
    const sill = half[0];
    // floor centre → under the left sill → up the left side to the top → down the right side → under the right sill
    const loop: V2[] = [[0, fy], [sill[0], fy], ...half, ...half.slice(0, S).reverse().map(([x, y]) => [-x, y] as V2), [-sill[0], fy]];
    loops.push(loop);
    const yc = (fy + sec.y[sec.crest]) / 2;
    rings.push(loop.map(([x, y]) => m.vert(x, y, z, x, y - yc, 0)));
  }
  for (const r of rings) r.push(r[0]);
  m.grid(rings);
  // caps
  for (const [k, sign] of [[0, -1], [zs.length - 1, 1]] as Array<[number, number]>) {
    const loop = loops[k], z = zs[k];
    const ids = loop.map(([x, y]) => m.vert(x, y, z, 0, 0, sign));
    for (let i = 1; i < ids.length - 1; i++) m.tri(ids[0], ids[i], ids[i + 1]);
  }
  // the diffuser's five fins below the floor: one flat triangle each (the material is double-sided)
  for (const fx of [-0.62, -0.22, 0, 0.22, 0.62]) {
    const a = m.vert(fx, Y_FLOOR + 0.002, Z_DIFFUSER + 0.02, 1, 0, 0), b = m.vert(fx, diffuserY(Z_TAIL) + 0.004, Z_TAIL + 0.004, 1, 0, 0), c = m.vert(fx, Y_FLOOR + 0.11, Z_TAIL + 0.004, 1, 0, 0);
    m.tri(a, b, c);
  }
  // wheels: hexagonal prisms (the tyre's contact shadow outside the body's)
  for (const spot of WHEELS) {
    const hw = spot.front ? TYRE_HALF.front : TYRE_HALF.back;
    const prism = new THREE.CylinderGeometry(WHEEL_R, WHEEL_R, hw * 2, 6, 1, false);
    m.add(prism, new THREE.Matrix4().makeTranslation(spot.x, WHEEL_Y, spot.z).multiply(new THREE.Matrix4().makeRotationZ(Math.PI / 2)));
  }
  hullGeometry = m.geometry();
  sectionCache.clear();
  return hullGeometry;
}

/**
 * The roadster: a white hypercar with a long low nose, a dark glass teardrop canopy, wide rear haunches, a full-width
 * red light bar and a black diffuser; four thrusters under the floor and two boosters in the tail. Smooth lofted
 * body with analytic normals, PBR clear-coat paint. The wheels fold flat into hover pads when it flies.
 */
export function buildRoadster(gradientMap: THREE.Texture, skin: Skin, logo?: THREE.Texture, opts: RoadsterOptions = {}): RoadsterParts {
  void gradientMap;                       // the PBR roadster does not use the toon ramp (kept for the API)
  const detail = opts.detail ?? "full";
  const game = detail === "game";
  const geo = carGeometry(detail);
  const env = studioEnvironment();
  const root = new THREE.Group();
  root.name = "roadster";
  const body = new THREE.Group();
  body.name = "roadster-body";
  root.add(body);

  const paint = new THREE.MeshPhysicalMaterial({ color: skin.body, roughness: 0.3, metalness: 0.05, clearcoat: 1, clearcoatRoughness: 0.04, envMap: env, envMapIntensity: 1 });
  const glass = new THREE.MeshPhysicalMaterial({ color: skin.glass, roughness: 0.05, metalness: 0.25, clearcoat: 1, clearcoatRoughness: 0.03, envMap: env, envMapIntensity: 1.0, transparent: true, opacity: 0.93 });
  const accent = new THREE.MeshStandardMaterial({ color: skin.accent, roughness: 0.35, metalness: 0.3, envMap: env, emissive: skin.accent, emissiveIntensity: 0.25 });
  const black = new THREE.MeshPhysicalMaterial({ color: 0x0b0c10, roughness: 0.22, metalness: 0.2, clearcoat: 1, clearcoatRoughness: 0.08, envMap: env });
  const dark = new THREE.MeshStandardMaterial({ color: 0x17181c, roughness: 0.85, metalness: 0.15, envMap: env, envMapIntensity: 0.4, side: THREE.DoubleSide });
  const interior = new THREE.MeshStandardMaterial({ color: 0x24262c, roughness: 0.7, metalness: 0.05, envMap: env, envMapIntensity: 0.5, side: THREE.DoubleSide });
  const seat = new THREE.MeshStandardMaterial({ color: 0xe9e5dc, roughness: 0.55, envMap: env, envMapIntensity: 0.6 });
  const bolster = new THREE.MeshStandardMaterial({ color: 0x1c1d22, roughness: 0.6, envMap: env, envMapIntensity: 0.6 });
  const chrome = new THREE.MeshStandardMaterial({ color: 0xe4e8ef, roughness: 0.12, metalness: 1, envMap: env });
  const lens = new THREE.MeshPhysicalMaterial({ color: 0x07080b, roughness: 0.06, metalness: 0.3, clearcoat: 1, clearcoatRoughness: 0.02, envMap: env, envMapIntensity: 1.2 });
  const white = new THREE.MeshBasicMaterial({ color: new THREE.Color(0xf2f8ff).multiplyScalar(2.4) });
  // a pure red kept near 1: three's ACES (input ×exposure/0.6) turns brighter reds orange; its halo is the glow ribbon
  const red = new THREE.MeshBasicMaterial({ color: new THREE.Color(0xff0012).multiplyScalar(1.15) });
  const glow = new THREE.MeshBasicMaterial({ color: 0xff0a24, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, depthWrite: false });
  const gap = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.55, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
  const intake = new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.55, metalness: 0.2, envMap: env, envMapIntensity: 0.5 });
  const screen = new THREE.MeshBasicMaterial({ color: 0x1a3550 });
  const nozzleGlow = new THREE.MeshStandardMaterial({ color: 0x1a1f26, roughness: 0.35, metalness: 0.85, envMap: env, emissive: 0x2a8fd0, emissiveIntensity: 0.28 });
  const tyreMat = new THREE.MeshStandardMaterial({ color: 0x18191c, roughness: 0.82, metalness: 0, envMap: env, envMapIntensity: 0.35 });
  const rimMat = new THREE.MeshStandardMaterial({ color: 0x41454d, roughness: 0.3, metalness: 0.9, envMap: env });
  const discMat = new THREE.MeshStandardMaterial({ color: 0x8e9299, roughness: 0.42, metalness: 0.9, envMap: env });
  const caliperMat = new THREE.MeshPhysicalMaterial({ color: 0xd3172a, roughness: 0.3, metalness: 0.1, clearcoat: 1, clearcoatRoughness: 0.1, envMap: env });

  const underTex = underbodyTexture();
  const floor = new THREE.MeshStandardMaterial({ color: 0xffffff, map: underTex, emissive: 0xffffff, emissiveMap: underTex, emissiveIntensity: 0.3, roughness: 0.75, metalness: 0.3, envMap: env, envMapIntensity: 0.6, side: THREE.DoubleSide });
  const byPart: Record<string, THREE.Material> = { paint, glass, black, dark, floor, interior, seat, bolster, accent, chrome, lens, white, red, glow, gap, intake, screen, nozzle: nozzleGlow };
  // (no renderOrder games: the transparent parts sort with the scene's own effects by distance)
  const gg = geo.game;
  let trimMat: THREE.MeshStandardMaterial | undefined, trimMesh: THREE.Mesh | undefined;
  if (game && gg) {
    // detail "game": paint (+ black, lenses), trim, glass, gaps + halo — four meshes instead of eighteen
    bodyPaintShader(paint);
    trimMat = trimMaterial(env, accent.color);
    for (const [key, g, material] of [["paint", gg.body, paint], ["trim", gg.trim, trimMat], ["glass", gg.glass, glass], ["fx", gg.fx, fxMaterial()]] as Array<[string, THREE.BufferGeometry, THREE.Material]>) {
      const mesh = new THREE.Mesh(g, material);
      mesh.name = `roadster-${key}`;
      body.add(mesh);
      if (key === "trim") trimMesh = mesh;
    }
  } else {
    for (const [key, g] of Object.entries(geo.parts)) {
      const mesh = new THREE.Mesh(g, byPart[key]);
      mesh.name = `roadster-${key}`;
      body.add(mesh);
    }
  }
  paint.userData.base = new THREE.Color(skin.body);
  // the Solana mark on the bonnet and doors, at its own aspect
  if (logo) {
    const img = logo.image as { width?: number; height?: number } | undefined;
    const aspect = img?.width && img?.height ? img.height / img.width : 1000 / 1148;
    const key = Math.round(aspect * 1000);
    let g = geo.decals.get(key);
    if (!g) { g = atDetail(detail, () => buildDecals(aspect)); geo.decals.set(key, g); sectionCache.clear(); }
    // matte vinyl on top of the clear coat: the mark keeps its own colours (no clear-coat sky glare, a soft and weak
    // highlight, a darker diffuse so full sun does not blow the gradient out; the self-light keeps it alive in shade)
    const decal = new THREE.MeshPhysicalMaterial({
      map: logo, color: 0x8c8c8c, transparent: true, depthWrite: false, roughness: 0.75, metalness: 0, specularIntensity: 0.4, envMap: env, envMapIntensity: 0.25,
      emissive: 0xffffff, emissiveMap: logo, emissiveIntensity: 0.3, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2
    });
    const mesh = new THREE.Mesh(g, decal);
    mesh.name = "roadster-decals";
    body.add(mesh);
  }

  // wheels: each a group pivoting at its centre (axle along x); the game spins rotation.x and folds rotation.z
  const wheels: THREE.Object3D[] = [];
  for (const spot of WHEELS) {
    const wg = spot.front ? geo.wheels.front : geo.wheels.back;
    const wheel = new THREE.Group();
    wheel.name = spot.name;
    wheel.position.set(spot.x, WHEEL_Y, spot.z);
    // folded flat (rotation.z = ±π/2) the outer face points down: the wheel then drops out of its arch into a hover pad
    const pad = new FoldDrop(wheel, Math.sign(spot.x));
    if (trimMat) {
      // detail "game": the wheel is drawn by the trim (its vertices placed by this group × pad, see trimMaterial)
      wheel.add(pad);
      wheel.userData.baseRotation = wheel.rotation.clone();
      wheels.push(wheel);
      body.add(wheel);
      continue;
    }
    const spinning = new THREE.Group();
    if (spot.x < 0) spinning.rotation.y = Math.PI;         // outer face out on the right side too
    spinning.add(new THREE.Mesh(wg.tyre, tyreMat), new THREE.Mesh(wg.rim, rimMat), new THREE.Mesh(wg.chrome, chrome), new THREE.Mesh(wg.disc, discMat), new THREE.Mesh(wg.hub, dark), new THREE.Mesh(wg.accent, accent));
    pad.add(spinning);
    // the caliper does not turn with the wheel: a holder undoes the spin every frame
    const holder = new CounterSpin(wheel);
    const caliper = new THREE.Mesh(wg.caliper, caliperMat);
    if (spot.x < 0) caliper.scale.x = -1;
    holder.add(caliper);
    pad.add(holder);
    wheel.add(pad);
    wheel.userData.baseRotation = wheel.rotation.clone();
    wheels.push(wheel);
    body.add(wheel);
  }

  // thrusters: four plumes under the floor, two boosters out of the tail (origin at the nozzle; scale.y = length)
  const tex = flameGradient();
  const sheathMat = new THREE.MeshBasicMaterial({ color: 0x5cc8ff, map: tex, transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  const coreMat = new THREE.MeshBasicMaterial({ color: 0xeafcff, map: tex, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  const discMatGlow = new THREE.MeshBasicMaterial({ color: 0xbff3ff, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  // detail "game": one mesh per plume (sheath + core + disc), the game's ×2.2 on the sheath already baked in
  const plumeMat = gg ? new THREE.MeshBasicMaterial({ vertexColors: true, map: tex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }) : undefined;
  if (plumeMat) {
    plumeMat.userData.hdr = true;
    plumeMat.forceSinglePass = true;                     // additive: one pass gives the same sum as three's back-then-front pair
  }
  const flame = (): THREE.Mesh => {
    if (gg && plumeMat) return new THREE.Mesh(gg.flame, plumeMat);
    const sheath = new THREE.Mesh(geo.flame.sheath, sheathMat);
    sheath.add(new THREE.Mesh(geo.flame.core, coreMat), new THREE.Mesh(geo.flame.disc, discMatGlow));
    return sheath;
  };
  const jets: THREE.Mesh[] = [];
  for (const [x, z] of JET_SPOTS) {
    const jet = flame();
    jet.position.set(x, Y_FLOOR - 0.065, z);
    jet.scale.set(1, 0.001, 1);
    jets.push(jet);
    body.add(jet);
  }
  const boosters: THREE.Mesh[] = [];
  for (const x of BOOSTER_X) {
    const b = flame();
    b.rotation.x = Math.PI / 2;                           // −y → −z: out of the back
    b.position.set(x, BOOSTER_Y, Z_TAIL - 0.045);
    b.scale.set(0.85, 0.001, 0.85);
    boosters.push(b);
    body.add(b);
  }

  // reflections: the scene's environment when it has one (scaled like the rest of the world), else the studio
  root.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.onBeforeRender = adoptEnvironment; });
  if (trimMat && trimMesh) {
    // the four wheels ride in the trim: their group × fold-pad matrices (body space) and spins, set before each draw
    const u = trimMat.userData.trim as TrimUniforms;
    const pads = wheels.map((w) => w.children[0]);
    trimMesh.onBeforeRender = function (this: THREE.Object3D, renderer, scene, camera, geometry, material) {
      wheels.forEach((w, i) => { u.uWheel.value[i].multiplyMatrices(w.matrix, pads[i].matrix); });
      u.uSpin.value.set(wheels[0].rotation.x, wheels[1].rotation.x, wheels[2].rotation.x, wheels[3].rotation.x);
      adoptEnvironment.call(this, renderer, scene, camera, geometry, material);
    };
  }

  // the sun's shadow: a ~460-triangle hull instead of the car's meshes (detail "game"; hidden on "full")
  const shadowProxy = new THREE.Mesh(shadowHull(), proxyMaterial());
  shadowProxy.name = "roadster-shadow";
  shadowProxy.castShadow = true;
  shadowProxy.visible = game;
  // detail "game": no real mesh casts, whoever sets castShadow later (the wheels ride in the trim's vertex shader, which
  // the shadow pass's depth material would not run: the trim must never cast)
  if (game) root.traverse((o) => { if ((o as THREE.Mesh).isMesh) Object.defineProperty(o, "castShadow", { get: () => false, set: () => undefined, configurable: true }); });
  body.add(shadowProxy);

  const setSkin = (s: Skin): void => {
    const finish = FINISH[s.id] ?? { metalness: 0.1, roughness: 0.3 };
    paint.map?.dispose();
    paint.map = s.pattern ? patternTexture(s.pattern, s) : null;
    paint.color.set(s.pattern ? "#ffffff" : s.body);
    paint.userData.base = paint.color.clone();
    // very light paints keep a little headroom when the renderer does no tone mapping (else the sunlit side clips flat)
    paint.userData.untoned = !s.pattern && paint.color.getHSL({ h: 0, s: 0, l: 0 }).l > 0.85 ? 0.86 : 1;
    paint.metalness = finish.metalness;
    paint.roughness = finish.roughness;
    paint.needsUpdate = true;
    accent.color.set(s.accent);
    accent.emissive.set(s.accent);
    glass.color.set(s.glass);
  };
  setSkin(skin);
  return { root, body, wheels, jets, boosters, length: LENGTH + 0.2, height: 1.25, shadowProxy, setSkin };
}

/** onBeforeRender for every lit part of the car: pick the environment to reflect and the paint's headroom. */
function adoptEnvironment(this: THREE.Object3D, renderer: THREE.WebGLRenderer, scene: THREE.Scene, _camera: THREE.Camera, _geometry: THREE.BufferGeometry, material: THREE.Material): void {
  const m = material as THREE.MeshStandardMaterial;
  if (!m.isMeshStandardMaterial) return;
  const env = scene.environment ?? studioEnvironment();
  if (m.envMap !== env) { m.envMap = env; m.needsUpdate = true; }
  const base = (m.userData.envBase as number | undefined) ?? (m.userData.envBase = m.envMapIntensity);
  m.envMapIntensity = scene.environment ? base * scene.environmentIntensity : base;
  const paintBase = m.userData.base as THREE.Color | undefined;
  if (paintBase) m.color.copy(paintBase).multiplyScalar(renderer.toneMapping === THREE.NoToneMapping ? (m.userData.untoned as number | undefined) ?? 1 : 1);
}

/** Keeps a child still while its parent wheel spins about x (the brake caliper). */
class CounterSpin extends THREE.Group {
  constructor(private readonly wheel: THREE.Object3D) { super(); }
  override updateMatrix(): void {
    this.rotation.x = -this.wheel.rotation.x;
    super.updateMatrix();
  }
}

/** Slides a wheel outward along its axle as it folds, so the folded wheel hangs below the body as a hover pad. */
const FOLD_DROP = 0.17;
class FoldDrop extends THREE.Group {
  constructor(private readonly wheel: THREE.Object3D, private readonly side: number) { super(); }
  override updateMatrix(): void {
    this.position.x = this.side * FOLD_DROP * Math.abs(Math.sin(this.wheel.rotation.z));
    super.updateMatrix();
  }
}

